import React, { useState, useEffect, useMemo, useCallback, useContext } from "react";
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  Grid,
  Chip,
  Card,
  CardContent,
  CardActionArea,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tooltip,
  Autocomplete,
  LinearProgress,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import PersonIcon from "@mui/icons-material/Person";
import StarIcon from "@mui/icons-material/Star";
import PlaceIcon from "@mui/icons-material/Place";
import EventIcon from "@mui/icons-material/Event";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import BasemapTiles from "../components/BasemapTiles";
import { MapContainer, Marker, Circle, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
  Annotation,
} from "react-simple-maps";
import DashboardLayout from "../layouts/DashboardLayout";
import api from "../api/client";
import { AuthContext } from "../context/AuthContext";
import { COUNTY_FIPS, REGIONS } from "../constants/regions";

// Counties GeoJSON URL (covers all US counties including Oklahoma)
const COUNTIES_GEO_URL =
  "https://raw.githubusercontent.com/plotly/datasets/master/geojson-counties-fips.json";

const KM_TO_MILES = 0.621371;
const METERS_TO_FEET = 3.28084;

const kmToMiles = (km) => {
  const value = Number(km);
  return Number.isFinite(value) ? value * KM_TO_MILES : null;
};

const milesToKm = (miles) => {
  const value = Number(miles);
  return Number.isFinite(value) ? value / KM_TO_MILES : null;
};

const metersToFeet = (meters) => {
  const value = Number(meters);
  return Number.isFinite(value) ? value * METERS_TO_FEET : null;
};

const feetToMeters = (feet) => {
  const value = Number(feet);
  return Number.isFinite(value) ? value / METERS_TO_FEET : null;
};

const ALL_TABS = [
  { label: "Patrol Calendar", value: "patrol-calendar" },
  { label: "Quick Links", value: "quick-links" },
  { label: "Policies", value: "policies" },
  { label: "Log Expense", value: "log-expense" },
  { label: "Region Map", value: "region-map" },
  { label: "ICS-205", value: "ics-205", commsOnly: true },
  { label: "Repeater Map", value: "repeater-map", commsOnly: true },
  { label: "Regional Frequencies", value: "regional-frequencies", commsOnly: true },
  { label: "TXDOT Cams", value: "txdot-cams" },
  { label: "Prayer", value: "prayer" },
  { label: "Awards", value: "awards" },
];

// Awards data
const AWARDS = [
  {
    id: "founding-member",
    title: "Founding Member",
    description: "Recognizes those who laid the foundation of the Texas Highway Rangers and shaped its mission from day one.",
    eligibility: "Original founding members only",
    category: "recognition",
    icon: "🏛️",
  },
  {
    id: "fema-ics-certified",
    title: "FEMA ICS Certified",
    description: "Issued upon completion of core FEMA Incident Command System training, demonstrating readiness for emergency coordination and disaster response.",
    eligibility: "Complete FEMA ICS-100, ICS-200, ICS-700, and ICS-800 courses",
    category: "certification",
    icon: "🎓",
  },
  {
    id: "national-tim-certified",
    title: "National TIM Training Certified",
    description: "Awarded to members who have completed the National Traffic Incident Management Responder Training Program, focused on safe, coordinated, and quick clearance of roadway incidents.",
    eligibility: "Complete 10-hour National TIM Responder Training",
    category: "certification",
    icon: "🚧",
  },
  {
    id: "roadway-leadership-certified",
    title: "Roadway Incident Response Leadership Certified",
    description: "Awarded upon successful completion of the leadership program covering all critical aspects of roadway incident response. This certification is required for those serving as region leaders or training instructors.",
    eligibility: "Complete 9-hour Roadway Incident Response Leadership course",
    category: "certification",
    icon: "👔",
  },
  {
    id: "runway-responder",
    title: "Runway Responder",
    description: "Because nobody rocks ANSI Class III quite like you. Safety… but make it stylish.",
    eligibility: "Wear a safety vest on every call",
    category: "achievement",
    icon: "🦺",
  },
  {
    id: "jumpstart-jedi",
    title: "Jumpstart Jedi",
    description: "Uses the Force (and proper polarity) to bring the dead… batteries… back to life.",
    eligibility: "Jumpstart 20 vehicles in 365 days",
    category: "achievement",
    icon: "⚡",
  },
  {
    id: "lug-nut-whisperer",
    title: "Lug Nut Whisperer",
    description: "Awarded for mastering the art of the 4-way lug wrench and always knowing which way is tight.",
    eligibility: "Perform 20 tire changes in 365 days",
    category: "achievement",
    icon: "🔧",
  },
  {
    id: "field-repair-wizard",
    title: "Field Repair Wizard",
    description: "Presented to the Ranger who fixed something critical with zip ties, duct tape, or sheer willpower.",
    eligibility: "Perform 30 minor repairs in 365 days",
    category: "achievement",
    icon: "🧙",
  },
  {
    id: "wrong-place-perfect-time",
    title: "Wrong Place, Perfect Time",
    description: "You weren't trying to find the incident. It found you.",
    eligibility: "First on scene of 10 accidents in 365 days",
    category: "achievement",
    icon: "🎯",
  },
  {
    id: "rope-wrangler",
    title: "Rope Wrangler",
    description: "For expertly pulling stranded vehicles with nothing but a tow rope, horsepower, and a healthy disregard for getting stuck yourself.",
    eligibility: "Pull/Tow/Recover 10 vehicles in 365 days",
    category: "achievement",
    icon: "🪢",
  },
  {
    id: "bumper-buddy",
    title: "Bumper Buddy",
    description: "For providing the most gentle shoves in public safety — like a hug, but with 4,000 pounds of steel.",
    eligibility: "Push/Pull/Tow/Recover 10 vehicles in 365 days",
    category: "achievement",
    icon: "🚗",
  },
  {
    id: "recovery-agent",
    title: "Recovery Agent",
    description: "Not every off-road adventure was planned. When tires leave the pavement, you're the one who gets them home.",
    eligibility: "Push/Pull/Tow/Recover 5 vehicles in 365 days",
    category: "achievement",
    icon: "🛻",
  },
  {
    id: "cooler-command",
    title: "Cooler Command",
    description: "You're not just providing water — you're delivering comfort, calm, and compassion when people need it most. Proud provider of liquid morale on hot Texas roads.",
    eligibility: "Provide bottled water/snacks 10 times in 365 days",
    category: "achievement",
    icon: "💧",
  },
  {
    id: "gear-goblin",
    title: "Gear Goblin",
    description: "For the Ranger who owns more lights, tools, and gear than some fire departments — and somehow still finds a reason to buy more. You've officially entered goblin territory. Welcome to the hoard.",
    eligibility: "Tier 1 - Spend $1,000 on Ranger equipment",
    category: "gear",
    icon: "👺",
  },
  {
    id: "supply-dragon",
    title: "Supply Dragon",
    description: "Your trunk is now a rolling armory. You don't just collect gear — you guard it. Awarded to the Ranger whose supply stash rivals a tactical outfitter's warehouse.",
    eligibility: "Tier 2 - Spend $2,000 on Ranger equipment",
    category: "gear",
    icon: "🐉",
  },
  {
    id: "lord-of-the-loadout",
    title: "Lord of the Loadout",
    description: "You are the final boss of roadside readiness. From tools to trauma kits, batteries to bolt cutters — if it exists, you already bought two. One to use. One just in case.",
    eligibility: "Tier 3 - Spend $3,000 on Ranger equipment",
    category: "gear",
    icon: "👑",
  },
  {
    id: "public-safety-partner",
    title: "Public Safety Partner",
    description: "Awarded to Rangers who actively serve in law enforcement, fire, EMS, dispatch, or emergency management outside of the Texas Highway Rangers. Your dedication to public safety doesn't stop with us — it's a calling you live out every day.",
    eligibility: "Verified employment in public safety",
    category: "recognition",
    icon: "🛡️",
  },
  {
    id: "1-year-service",
    title: "1 Year Service Recognition",
    description: "For the Rangers who've been with us through all four seasons — rain, shine, heat waves, and Texas traffic. One year stronger, one year prouder.",
    eligibility: "Active member for 1 year (365 days)",
    category: "service",
    icon: "🎖️",
  },
  {
    id: "2-year-service",
    title: "2 Year Service Recognition",
    description: "Two years of dedication, two years of showing up when it counts. You're not just a Ranger — you're a cornerstone.",
    eligibility: "Active member for 2 years (730 days)",
    category: "service",
    icon: "🎖️",
  },
  {
    id: "3-year-service",
    title: "3 Year Service Recognition",
    description: "Three years on the road, countless assists in the rearview. Your commitment inspires the next generation of Rangers.",
    eligibility: "Active member for 3 years (1,095 days)",
    category: "service",
    icon: "🎖️",
  },
  {
    id: "4-year-service",
    title: "4 Year Service Recognition",
    description: "Four years of service, four years of being the calm in someone's storm. A veteran in every sense of the word.",
    eligibility: "Active member for 4 years (1,460 days)",
    category: "service",
    icon: "🎖️",
  },
  {
    id: "5-year-service",
    title: "5 Year Service Recognition",
    description: "Half a decade of selfless service. You've seen it all, done it all, and still show up ready to help. A true pillar of the Texas Highway Rangers.",
    eligibility: "Active member for 5 years (1,825 days)",
    category: "service",
    icon: "🏆",
  },
  {
    id: "lone-star-award",
    title: "Lone Star Award",
    description: "Yearly award given to the Ranger who embodies what it means to serve with excellence. Tireless, humble, dependable. A true light on the roadside and a leader among peers.",
    eligibility: "Nominated and selected by leadership annually",
    category: "recognition",
    icon: "⭐",
  },
  {
    id: "critical-response-commendation",
    title: "Critical Response Commendation",
    description: "Recognizing decisive action and unwavering composure during a critical incident — when every second counted. Awarded to the Ranger who preserved what matters most: another chance, another heartbeat, another tomorrow.",
    eligibility: "Perform verified life-saving action",
    category: "recognition",
    icon: "💗",
  },
  {
    id: "target-acquired",
    title: "Target Acquired",
    description: "You've developed a sixth sense for spotting stranded drivers — with this many confirmed sightings, you might as well be flying a drone. Your sharp eyes help the team prioritize real needs over empty hazards and keep our response focused where it matters most.",
    eligibility: "Log 75 GOA (Gone on Arrival) incidents in 365 days",
    category: "achievement",
    icon: "🔭",
  },
  {
    id: "fuel-fairy",
    title: "Fuel Fairy",
    description: "When the tank hits empty, you're the one who fills it back up. Guardian angel of the gas-less.",
    eligibility: "Perform 5 fuel deliveries in 365 days",
    category: "achievement",
    icon: "🛢️",
  },
  {
    id: "traffic-tamer",
    title: "Traffic Tamer",
    description: "Cones, flares, and hand signals — you control the chaos so others can pass safely.",
    eligibility: "Perform 25 traffic control assists in 365 days",
    category: "achievement",
    icon: "🚦",
  },
  {
    id: "road-scholar",
    title: "Road Scholar",
    description: "You don't just respond — you diagnose. Part mechanic, part detective.",
    eligibility: "Perform 15 diagnosis assists in 365 days",
    category: "achievement",
    icon: "📚",
  },
  {
    id: "century-club",
    title: "Century Club",
    description: "100 incidents. Triple digits. You're officially in the club.",
    eligibility: "Complete 100 total incidents in 365 days",
    category: "achievement",
    icon: "💯",
  },
  {
    id: "mentor-badge",
    title: "Mentor Badge",
    description: "You don't just serve — you teach. Awarded for guiding new Rangers through their first responses and sharing the wisdom that keeps us all safer.",
    eligibility: "Train or mentor new Rangers (leadership nomination)",
    category: "recognition",
    icon: "👨‍🏫",
  },
  {
    id: "10-year-service",
    title: "10 Year Service Recognition",
    description: "A decade of dedication. Through good times and hard times, you've been a constant presence on Texas roads. Legend status achieved.",
    eligibility: "Active member for 10 years (3,650 days)",
    category: "service",
    icon: "🏅",
  },
  {
    id: "air-ace",
    title: "Air Ace",
    description: "Keeping tires inflated and spirits high. You've mastered the art of the perfect PSI.",
    eligibility: "Perform 15 air tire assists in 365 days",
    category: "achievement",
    icon: "🎈",
  },
  {
    id: "good-neighbor",
    title: "Good Neighbor",
    description: "Sometimes people just need a lift. You're the one who gives it — no questions asked, just kindness.",
    eligibility: "Provide 5 courtesy rides in 365 days",
    category: "achievement",
    icon: "🚐",
  },
  {
    id: "fire-fighter",
    title: "Fire Fighter",
    description: "Cool under pressure, literally. When flames threaten, you're ready with the extinguisher.",
    eligibility: "Use fire extinguisher 1 time in 365 days",
    category: "achievement",
    icon: "🧯",
  },
  {
    id: "master-mechanic",
    title: "Master Mechanic",
    description: "When minor won't cut it, you go major. Complex repairs don't scare you — they challenge you.",
    eligibility: "Perform 10 major repairs in 365 days",
    category: "achievement",
    icon: "🔩",
  },
  {
    id: "debris-destroyer",
    title: "Debris Destroyer",
    description: "From mattresses to mufflers, you keep Texas roads clear. One piece of debris at a time.",
    eligibility: "Remove debris 20 times in 365 days",
    category: "achievement",
    icon: "🧹",
  },
  {
    id: "bridge-builder",
    title: "Bridge Builder",
    description: "Language barriers don't stop you from helping. You bridge the gap when communication matters most.",
    eligibility: "Provide translation assist 10 times in 365 days",
    category: "achievement",
    icon: "🌉",
  },
  {
    id: "motor-one-moving",
    title: "Motor One Moving",
    description: "Guiding the vulnerable safely to their destination. When someone needs an escort, you lead the way.",
    eligibility: "Provide 10 safety escorts in 365 days",
    category: "achievement",
    icon: "🚨",
  },
  {
    id: "hat-trick",
    title: "Hat Trick",
    description: "Three incidents in one day? That's a hat trick. And you've done it more than once.",
    eligibility: "Complete 3+ incidents in a single day, 5 times in 365 days",
    category: "achievement",
    icon: "🎩",
  },
  {
    id: "iron-ranger",
    title: "Iron Ranger",
    description: "500 career incidents. You're not just part of the team — you are the team. Through heat, storms, holidays, and late nights, you've answered the call half a thousand times. Iron doesn't bend. Neither do you.",
    eligibility: "Complete 500 total career incidents",
    category: "recognition",
    icon: "🦾",
  },
  {
    id: "community-ambassador",
    title: "Community Ambassador",
    description: "Awarded to the Ranger who goes beyond the roadside to represent the Highway Rangers at public events, community outreach programs, and educational demonstrations. You're the face of the mission.",
    eligibility: "Represent the Highway Rangers at 3+ public events or outreach activities in a year (leadership nomination)",
    category: "recognition",
    icon: "🤝",
  },
  {
    id: "recruiter",
    title: "Recruiter",
    description: "Every great organization grows because someone believes in the mission enough to share it. You've brought new Rangers into the fold, growing our ranks and strengthening our impact.",
    eligibility: "Successfully recruit and onboard 3+ new Rangers (leadership nomination)",
    category: "recognition",
    icon: "📣",
  },
  {
    id: "above-and-beyond",
    title: "Above & Beyond",
    description: "When the situation called for more, you gave everything. This award recognizes a single incident where your actions went far beyond what was expected — selfless, decisive, and extraordinary.",
    eligibility: "Go significantly beyond the call of duty on a single incident (leadership nomination)",
    category: "recognition",
    icon: "🌟",
  },
  {
    id: "unit-citation",
    title: "Unit Citation",
    description: "Awarded to Rangers who participated in a multi-unit response that received a formal commendation. When the team comes together under pressure and delivers exceptional results, everyone on scene earns this ribbon.",
    eligibility: "Participate in a commended multi-unit response (leadership nomination)",
    category: "recognition",
    icon: "🎗️",
  },
  {
    id: "night-owl",
    title: "Night Owl",
    description: "While most people are asleep, you're out on the road keeping Texas safe. The night shift isn't for everyone — but for you, it's where you shine brightest.",
    eligibility: "Complete 10 incidents between 10 PM and 6 AM in 365 days",
    category: "achievement",
    icon: "🦉",
  },
  {
    id: "weekend-warrior",
    title: "Weekend Warrior",
    description: "Saturdays and Sundays are for rest — unless you're a Ranger. You sacrifice your weekends so that stranded motorists don't have to wait until Monday for help.",
    eligibility: "Complete 50 incidents on Saturday or Sunday in 365 days",
    category: "achievement",
    icon: "🗓️",
  },
  {
    id: "highway-guardian",
    title: "Highway Guardian",
    description: "250 incidents in a single year. You don't just patrol the highway — you guard it. An extraordinary level of commitment that puts you in a class of your own.",
    eligibility: "Complete 250 total incidents in 365 days",
    category: "achievement",
    icon: "🛣️",
  },
  {
    id: "swiss-army-ranger",
    title: "Swiss Army Ranger",
    description: "Jumpstarts, tire changes, fuel deliveries, traffic control, towing — you do it all. A true jack-of-all-trades who's ready for anything the road throws at you.",
    eligibility: "Perform 5+ different service types in a single month, 6 months in 365 days",
    category: "achievement",
    icon: "🇨🇭",
  },
  {
    id: "holiday-hero",
    title: "Holiday Hero",
    description: "While families gather and fireworks fly, you're out on the road keeping people safe. Thanksgiving, Christmas, New Year's, the Fourth — you show up when it matters most.",
    eligibility: "Respond to 5 incidents on major holidays in 365 days",
    category: "achievement",
    icon: "🎄",
  },
  {
    id: "pacemaker",
    title: "Pacemaker",
    description: "Consistency is the hallmark of a true professional. Thirty straight days of responding to at least one incident — rain or shine, weekday or weekend. You never miss a beat.",
    eligibility: "Maintain a response streak of 30 consecutive days with at least 1 incident",
    category: "achievement",
    icon: "💓",
  },
  {
    id: "500-career-incidents",
    title: "500 Career Incidents",
    description: "Half a thousand calls answered. This milestone represents years of dedication, countless hours on the road, and an unwavering commitment to serving others.",
    eligibility: "Reach 500 total career incidents",
    category: "service",
    icon: "🎯",
  },
  {
    id: "1000-career-incidents",
    title: "1,000 Career Incidents",
    description: "One thousand incidents. A number that speaks for itself. You are a pillar of the Texas Highway Rangers and a living legend of roadside service.",
    eligibility: "Reach 1,000 total career incidents",
    category: "service",
    icon: "🏛️",
  },
];

const EXPENSE_CATEGORIES = [
  { id: 1, name: "Roadside Assistance & Motorist Care", description: "Water, blankets, food for stranded motorists. Emergency supplies provided to the public. Safety items given directly to motorists." },
  { id: 2, name: "Safety & Visibility Equipment", description: "Cones, flares, LED beacons. High-visibility lighting. Scene safety gear." },
  { id: 3, name: "Communications & Technology", description: "Radios, antennas, mounts. IoT devices, trackers, dash cameras. Software, apps, subscriptions related to operations." },
  { id: 4, name: "Tools & Operational Equipment", description: "Jump packs, air compressors. Hand tools. Recovery and roadside equipment." },
  { id: 5, name: "Fuel & Fluids", description: "Fuel for response vehicles. Oil, DEF, coolant, washer fluid." },
  { id: 6, name: "Vehicle Maintenance & Repairs", description: "Routine maintenance. Tires, brakes, parts. Emergency repairs during operations." },
  { id: 7, name: "Uniforms & Identification", description: "Shirts, jackets, hats. High-visibility vests. Badges, patches, identifiers." },
  { id: 8, name: "Training & Certifications", description: "Safety training. First aid / roadside safety courses. Required or mission-related education." },
  { id: 9, name: "Food & Sustenance", description: "Meals during extended operations. Water/snacks for volunteers during long shifts." },
  { id: 10, name: "Administrative & Operations", description: "Printing, forms, signage. Web services, domains." },
  { id: 11, name: "Other / Miscellaneous", description: "One-off or rare expenses that don't fit elsewhere. Requires a description.", requiresNotes: true },
];

const POLICY_DOCUMENTS = [
  {
    id: "sop",
    title: "Standard Operating Procedures",
    description: "General SOPs for Highway Ranger operations",
    lastUpdated: "January 1, 2025",
    content: `
## 1. Purpose
These Standard Operating Procedures (SOPs) establish guidelines for all Highway Ranger personnel to ensure consistent, safe, and effective operations across all districts.

## 2. Scope
These procedures apply to all Highway Ranger personnel, including full-time employees, part-time staff, and contractors operating under the Highway Ranger program.

## 3. Daily Operations

### 3.1 Shift Start Procedures
- Report to designated station at least 15 minutes before shift start
- Complete vehicle inspection using the pre-shift checklist
- Verify all equipment is present and functional
- Check radio communications
- Review any outstanding incidents from previous shift
- Log into the Ranger Vault system and update status to "Available"

### 3.2 Patrol Duties
- Maintain continuous patrol of assigned route segments
- Respond promptly to all dispatched calls
- Monitor traffic conditions and report hazards
- Assist stranded motorists as encountered
- Document all activities in the Ranger Vault system

### 3.3 Shift End Procedures
- Complete all incident reports before end of shift
- Return vehicle to designated location
- Refuel vehicle if below 3/4 tank
- Report any equipment issues or damage
- Update status to "Off Duty" in Ranger Vault

## 4. Communication Protocols

### 4.1 Radio Usage
- Use clear, professional language at all times
- Use proper call signs and 10-codes
- Keep transmissions brief and to the point
- Monitor assigned channel continuously while on duty

### 4.2 Dispatch Coordination
- Acknowledge all dispatch calls within 30 seconds
- Provide accurate ETA to incident location
- Report arrival on scene immediately
- Provide regular status updates during extended incidents

## 5. Documentation Requirements
- All incidents must be documented in Ranger Vault within 30 minutes of clearance
- Include accurate time stamps for all activities
- Attach photos when applicable
- Note any follow-up actions required

## 6. Chain of Command
1. Field Supervisor
2. District Manager
3. Regional Director
4. Program Administrator

## 7. Revision History
- Version 1.0 - January 1, 2025 - Initial release
    `,
  },
  {
    id: "safety",
    title: "Safety Guidelines",
    description: "Safety protocols and best practices",
    lastUpdated: "January 1, 2025",
    content: `
## 1. Personal Safety

### 1.1 Personal Protective Equipment (PPE)
All Highway Rangers must wear appropriate PPE at all times while on duty:
- **High-visibility vest or jacket** - Class 3 ANSI-rated, must be worn anytime outside the vehicle
- **Safety glasses** - When working with equipment or debris
- **Work gloves** - When handling equipment, debris, or assisting with vehicles
- **Steel-toed boots** - Required footwear for all field personnel
- **Hard hat** - Required in construction zones or overhead hazard areas

### 1.2 Traffic Safety
- Always position vehicle to create a barrier between you and traffic
- Use all available warning lights when stopped on roadway
- Never turn your back to oncoming traffic
- Maintain situational awareness at all times
- Use traffic cones to create a safe work zone when possible

### 1.3 Vehicle Safety
- Always wear seatbelt while vehicle is in motion
- Obey all traffic laws, including speed limits
- Use emergency lights only when responding to incidents or providing assistance
- Conduct pre-trip inspection before each shift
- Report any vehicle defects immediately

## 2. Incident Scene Safety

### 2.1 Approaching an Incident
- Reduce speed when approaching any incident scene
- Assess the scene before exiting your vehicle
- Park in a position that provides protection
- Activate all warning lights
- Exit vehicle on the side away from traffic when possible

### 2.2 Scene Assessment
Before taking any action, assess:
- Traffic hazards
- Fire or explosion risk
- Hazardous materials
- Downed power lines
- Unstable vehicles
- Aggressive individuals

### 2.3 Working in Traffic
- Never assume drivers see you
- Keep escape routes in mind at all times
- Watch for vehicles entering the work zone
- Use spotters when backing up
- Minimize time spent in travel lanes

## 3. Emergency Procedures

### 3.1 Personal Injury
- If injured, move to a safe location if possible
- Notify dispatch immediately
- Request emergency medical services if needed
- Document the incident thoroughly

### 3.2 Vehicle Accident
- Check for injuries first
- Move to safe location if possible
- Contact dispatch and supervisor immediately
- Do not admit fault or discuss details with other parties
- Document with photos and written account

### 3.3 Hostile Situations
- Do not engage with aggressive individuals
- Return to your vehicle if threatened
- Contact law enforcement immediately
- Leave the scene if necessary for your safety
- Document and report all incidents

## 4. Health and Wellness

### 4.1 Heat Safety
- Stay hydrated - drink water regularly
- Take breaks in air-conditioned vehicle
- Recognize signs of heat exhaustion
- Wear appropriate clothing for conditions

### 4.2 Cold Weather Safety
- Dress in layers
- Keep emergency supplies in vehicle
- Watch for signs of hypothermia
- Limit exposure time in extreme cold

## 5. Emergency Contact Numbers
- Dispatch: Available through radio
- Supervisor: Contact through dispatch
- Emergency: 911
- Poison Control: 1-800-222-1222
    `,
  },
  {
    id: "incident-response",
    title: "Incident Response Protocol",
    description: "Step-by-step incident response procedures",
    lastUpdated: "January 1, 2025",
    content: `
## 1. Incident Categories

### Category 1: Minor Incidents
- Disabled vehicles (no lane blockage)
- Flat tires
- Out of fuel
- Minor mechanical issues
**Target Response Time:** 15 minutes
**Target Clearance Time:** 30 minutes

### Category 2: Moderate Incidents
- Disabled vehicles blocking lanes
- Minor accidents (no injuries)
- Debris on roadway
- Stalled vehicles in travel lanes
**Target Response Time:** 10 minutes
**Target Clearance Time:** 45 minutes

### Category 3: Major Incidents
- Accidents with injuries
- Multi-vehicle accidents
- Hazardous material spills
- Fatality incidents
**Target Response Time:** Immediate
**Target Clearance Time:** As required by emergency services

## 2. Response Procedures

### 2.1 Receiving a Call
1. Acknowledge dispatch within 30 seconds
2. Note incident location and type
3. Provide estimated time of arrival
4. Update status to "En Route" in Ranger Vault

### 2.2 En Route
1. Activate emergency lights if responding to active incident
2. Obey traffic laws - do not exceed safe speeds
3. Plan your approach and parking position
4. Notify dispatch of any delays

### 2.3 Arrival on Scene
1. Update status to "On Scene" in Ranger Vault
2. Assess scene safety before exiting vehicle
3. Position vehicle for traffic protection
4. Activate all warning equipment

### 2.4 Scene Management
1. Ensure scene safety first
2. Check for injuries - request EMS if needed
3. Assess vehicle condition and needs
4. Determine appropriate assistance

## 3. Specific Incident Types

### 3.1 Disabled Vehicle - Shoulder
1. Position vehicle behind disabled vehicle with offset
2. Activate arrow board if available
3. Approach and assess situation
4. Offer assistance (jump start, tire change, fuel, tow)
5. If tow needed, arrange through dispatch
6. Stay on scene until motorist is safely mobile or tow arrives

### 3.2 Disabled Vehicle - Lane Blockage
1. Request traffic control assistance if needed
2. Position vehicle for maximum traffic protection
3. Deploy traffic cones if safe to do so
4. Attempt to move vehicle to shoulder
5. If vehicle cannot be moved, request tow immediately
6. Direct traffic as needed until cleared

### 3.3 Debris on Roadway
1. Assess debris size and location
2. If safe, remove debris from roadway
3. Use proper lifting techniques
4. For large debris, request assistance
5. Document type and location of debris

### 3.4 Accident - No Injuries
1. Check all occupants for injuries
2. Request law enforcement if not already dispatched
3. Assist with moving vehicles from travel lanes if safe
4. Set up traffic control
5. Assist with information exchange if requested
6. Document scene with photos

### 3.5 Accident - With Injuries
1. Request EMS immediately if not already dispatched
2. Do not move injured persons unless immediate danger
3. Provide basic first aid if trained and safe
4. Secure the scene for emergency responders
5. Assist with traffic control
6. Follow instructions from emergency services

## 4. Documentation

### 4.1 Required Information
- Incident type and location
- Time of dispatch, arrival, and clearance
- Services provided
- Vehicles involved (make, model, color, plate)
- Tow company used (if applicable)
- Other agencies on scene

### 4.2 Photos
Take photos of:
- Overall scene
- Vehicle positions
- Damage
- Debris
- Road conditions

## 5. Clearance Procedures
1. Ensure all vehicles are safely mobile or removed
2. Remove all traffic control equipment
3. Verify roadway is clear of debris
4. Update status to "Available" in Ranger Vault
5. Complete incident report within 30 minutes
    `,
  },
  {
    id: "equipment",
    title: "Equipment Usage Policy",
    description: "Proper use and maintenance of equipment",
    lastUpdated: "January 1, 2025",
    content: `
## 1. Vehicle Equipment

### 1.1 Standard Vehicle Equipment
All Highway Ranger vehicles must be equipped with:
- Emergency lighting (lightbar, strobes)
- Arrow board or directional warning
- Two-way radio
- First aid kit
- Fire extinguisher (10 lb minimum)
- Traffic cones (minimum 6)
- Reflective triangles (set of 3)
- Push bumper
- Jump start equipment
- Basic tool kit
- Flashlights (2 minimum)
- Safety flares or LED flares
- Fuel can (empty, 2.5 gallon)
- Tire changing equipment
- Tow strap
- Absorbent material for fluid spills

### 1.2 Vehicle Inspection
Daily pre-shift inspection must include:
- All lights and signals functional
- Emergency equipment operational
- Tires in good condition with proper inflation
- Fluid levels checked
- Cleanliness of vehicle interior and exterior
- All equipment present and functional

### 1.3 Vehicle Maintenance
- Report any issues immediately to supervisor
- Do not operate vehicle with safety defects
- Keep vehicle clean inside and out
- Refuel when tank reaches 1/4 level
- Document all maintenance and repairs

## 2. Emergency Equipment

### 2.1 Lighting Equipment
- Test all emergency lights at start of shift
- Use appropriate lighting for situation
- Arrow board: Use for lane closures
- Strobes: Use when stopped on roadway
- Report any malfunctions immediately

### 2.2 Traffic Control Equipment
**Traffic Cones:**
- Inspect for damage before use
- Place at proper intervals (see traffic control manual)
- Retrieve all cones after incident clearance
- Clean and store properly

**Reflective Triangles:**
- Place at 10, 100, and 200 feet behind vehicle
- Use in low visibility conditions
- Retrieve before departing scene

### 2.3 Jump Start Equipment
- Inspect cables for damage before each use
- Follow proper connection sequence
- Never jump start near fuel leaks
- Allow recipient vehicle to run 5 minutes before disconnecting

### 2.4 Tire Changing Equipment
- Inspect jack and tools before use
- Ensure vehicle is on stable, level surface
- Use wheel chocks when available
- Never work under vehicle supported only by jack

## 3. Personal Equipment

### 3.1 Required Personal Equipment
- High-visibility safety vest (Class 3)
- Safety glasses
- Work gloves
- Steel-toed boots
- Flashlight
- Notepad and pen

### 3.2 Communication Equipment
- Portable radio (if issued)
- Cell phone for backup communication
- Keep devices charged at all times

## 4. Equipment Care and Storage

### 4.1 General Care
- Clean equipment after each use
- Store in designated locations
- Report damaged or missing equipment immediately
- Do not modify equipment without authorization

### 4.2 Inventory
- Conduct weekly equipment inventory
- Report shortages to supervisor
- Return borrowed equipment promptly

## 5. Prohibited Uses
- Personal use of any equipment
- Lending equipment to non-authorized personnel
- Using damaged equipment
- Modifying equipment without approval
- Removing equipment from vehicle without authorization
    `,
  },
  {
    id: "uniform",
    title: "Uniform Standards",
    description: "Dress code and uniform requirements",
    lastUpdated: "January 1, 2025",
    content: `
## 1. Purpose
The Highway Ranger uniform creates a professional appearance, ensures visibility and safety, and allows the public to easily identify authorized personnel.

## 2. Required Uniform Components

### 2.1 Shirt
- Official Highway Ranger polo shirt OR
- Official Highway Ranger button-down shirt
- Must have official logo/patch
- Shirt must be tucked in at all times
- Undershirts must be plain white or black (no visible logos)

### 2.2 Pants
- Dark navy or black tactical/work pants
- No jeans, shorts, or cargo pants with excessive pockets
- Must be clean and free of holes or excessive wear
- Belt required - black or brown leather/tactical

### 2.3 Footwear
- Steel-toed work boots required
- Black or brown leather/synthetic
- Boots must be in good repair
- No athletic shoes, sandals, or open-toed footwear

### 2.4 Safety Vest
- Class 3 ANSI-rated high-visibility vest
- Must be worn anytime outside vehicle during operations
- Must have reflective striping
- Keep clean and in good repair

### 2.5 Outerwear (Cold Weather)
- Official Highway Ranger jacket (if issued)
- High-visibility rain gear
- Black or navy fleece/sweater (no logos other than official)
- Safety vest must be worn over any outerwear

### 2.6 Headwear (Optional)
- Official Highway Ranger cap or hat
- No personal hats with logos
- Hard hat required in construction zones

## 3. Grooming Standards

### 3.1 General Appearance
- Clean, neat, and professional appearance required
- Personal hygiene must be maintained
- Hair must be clean and neatly groomed
- Beards/mustaches permitted if neatly trimmed

### 3.2 Jewelry
- Wedding/engagement rings permitted
- Small stud earrings permitted
- No dangling jewelry that could pose safety hazard
- Necklaces must be worn under shirt

### 3.3 Tattoos
- Visible tattoos should not be offensive
- Face and neck tattoos may require coverage
- Consult supervisor if uncertain

## 4. Identification

### 4.1 ID Badge
- Official ID badge must be worn at all times while on duty
- Badge must be visible and readable
- Report lost or damaged badges immediately
- Do not alter or deface badge

### 4.2 Name Tag
- Name tag must be worn on shirt (if issued)
- Keep clean and readable

## 5. Uniform Care

### 5.1 Maintenance
- Uniforms must be clean and pressed
- Replace worn or damaged items promptly
- Store uniforms properly when off duty
- Follow washing instructions on labels

### 5.2 Replacement
- Report uniform needs to supervisor
- Return worn items when receiving replacements
- Uniforms remain property of the department

## 6. Non-Compliance
Failure to maintain proper uniform standards may result in:
- Verbal warning
- Written warning
- Being sent home to correct deficiency
- Disciplinary action for repeated violations

## 7. Special Circumstances
- Reasonable accommodations available for religious or medical needs
- Contact supervisor to discuss specific requirements
- Document any approved exceptions
    `,
  },
];

const QUICK_LINKS_COMMON = [
  {
    title: "National Weather Service",
    description: "Weather forecasts and alerts",
    url: "https://www.weather.gov",
  },
  {
    title: "FHWA Traffic Incident Management",
    description: "Federal Highway Administration TIM resources",
    url: "https://ops.fhwa.dot.gov/eto_tim_pse/about/tim.htm",
  },
  {
    title: "Waze Live Map",
    description: "Real-time traffic and road reports",
    url: "https://www.waze.com/live-map",
  },
];

const QUICK_LINKS_TEXAS = [
  {
    title: "TXDOT Website",
    description: "Texas Department of Transportation official site",
    url: "https://www.txdot.gov",
  },
  {
    title: "DriveTexas",
    description: "Real-time Texas road conditions",
    url: "https://drivetexas.org",
  },
  {
    title: "TX-511",
    description: "Texas traffic & travel information",
    url: "https://www.my511.org",
  },
];

const QUICK_LINKS_SC = [
  {
    title: "SCDOT Website",
    description: "South Carolina Department of Transportation official site",
    url: "https://www.scdot.org",
  },
  {
    title: "511SC",
    description: "Real-time South Carolina road conditions",
    url: "https://www.511sc.org",
  },
  {
    title: "SC Highway Patrol",
    description: "South Carolina Highway Patrol information",
    url: "https://www.schp.sc.gov",
  },
];

// ICS-205 Radio Communications Plan Data (default/fallback)
const DEFAULT_ICS_205_DATA = {
  incidentName: "Texas Highway Rangers - Dallas District",
  operationalPeriod: {
    dateFrom: "Daily Operations",
    dateTo: "Ongoing",
    timeFrom: "0600",
    timeTo: "2200",
  },
  basicRadioChannelUse: [
    {
      zone: "HWY RANGERS",
      channel: "Dallas Primary",
      function: "Dallas Repeater",
      assignment: "Repeated Ops",
      rxFreq: "453.175",
      rxTone: "203.5 PL",
      txFreq: "458.175",
      txTone: "203.5 PL",
      mode: "A",
      remarks: "Secondary/Emergency Use Only",
    },
    {
      zone: "HWY RANGERS",
      channel: "Dallas Direct",
      function: "Dallas Repeater Simplex",
      assignment: "Repeater Simplex",
      rxFreq: "453.175",
      rxTone: "203.5 PL",
      txFreq: "",
      txTone: "",
      mode: "A",
      remarks: "Simplex",
    },
  ],
  specialInstructions: [
    "Encryption is OPTIONAL on Metro 1-3 and Rural 1-3 channels using AES256. For keyloading, contact the Communications Unit Leader.",
    "Dallas Primary Repeater is to be used on a SECONDARY/BACKUP/EMERGENCY basis only. This repeater is owned and operated by AMR Dallas Ambulances; however, Highway Rangers have authority to use it as needed for operations.",
    "All radio communications shall be absolutely professional at all times. Keep transmissions brief, clear, and to the point.",
    "All transmissions must include unit identifier.",
    "All non-emergency usage of Dallas Primary must be approved by the Communications Unit Leader.",
  ],
  preparedBy: {
    name: "Drake Robinson",
    position: "Communications Unit Leader",
    date: new Date().toLocaleDateString(),
    time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
  },
};

function Resources() {
  const { user } = useContext(AuthContext);
  const isSupervisorOrAdmin = user?.role === "supervisor" || user?.role === "admin";
  const canViewComms = !!user?.canViewComms || user?.role === "admin";

  const visibleTabs = useMemo(
    () => ALL_TABS.filter((t) => !t.commsOnly || canViewComms),
    [canViewComms]
  );

  const [activeTab, setActiveTab] = useState(ALL_TABS[0].value);
  const [expenseForm, setExpenseForm] = useState({
    purchaseDate: new Date().toISOString().split("T")[0],
    category: "",
    itemDescription: "",
    amountDollars: "",
    notes: "",
  });
  const [expenseReceipt, setExpenseReceipt] = useState(null);
  const [expenseSubmitting, setExpenseSubmitting] = useState(false);
  const [expenseSubmitted, setExpenseSubmitted] = useState(false);
  const [expenseError, setExpenseError] = useState(null);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [policyDialogOpen, setPolicyDialogOpen] = useState(false);

  // Dynamic Resources from API
  const [dynamicQuickLinks, setDynamicQuickLinks] = useState([]);
  const [dynamicPolicies, setDynamicPolicies] = useState([]);
  const [dynamicIcs205Data, setDynamicIcs205Data] = useState(null);
  const [resourcesLoading, setResourcesLoading] = useState(false);

  // Region Map state
  const [allUsers, setAllUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [regionDialogOpen, setRegionDialogOpen] = useState(false);

  // Patrol Calendar state
  const [patrolEvents, setPatrolEvents] = useState([]);
  const [patrolEventsLoading, setPatrolEventsLoading] = useState(false);
  const [patrolCalendarAlert, setPatrolCalendarAlert] = useState(null);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [eventDialogMode, setEventDialogMode] = useState("create"); // "create" or "edit"
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventForm, setEventForm] = useState({
    title: "",
    description: "",
    eventType: "Other",
    imageUrl: "",
    location: "",
    locationTitle: "",
    latitude: "",
    longitude: "",
    region: "",
    radioChannel: "",
    startTimeUtc: "",
    endTimeUtc: "",
  });
  const [signupDialogOpen, setSignupDialogOpen] = useState(false);
  const [signupTargetEvent, setSignupTargetEvent] = useState(null);
  const [availableUsersForSignup, setAvailableUsersForSignup] = useState([]);
  const [selectedUserForSignup, setSelectedUserForSignup] = useState(null);
  const [signupNotes, setSignupNotes] = useState("");
  const [showPastEvents, setShowPastEvents] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);

  // Award Eligibility Checker state
  const [awardSearchRangerId, setAwardSearchRangerId] = useState("");
  const [awardEligibilityData, setAwardEligibilityData] = useState(null);
  const [awardEligibilityLoading, setAwardEligibilityLoading] = useState(false);
  const [awardEligibilityError, setAwardEligibilityError] = useState(null);

  // Radio Repeater state
  const [repeaters, setRepeaters] = useState([]);
  const [repeatersLoading, setRepeatersLoading] = useState(false);
  const [repeaterDialogOpen, setRepeaterDialogOpen] = useState(false);
  const [repeaterDialogMode, setRepeaterDialogMode] = useState("create");
  const [editRepeater, setEditRepeater] = useState(null);
  const [repeaterForm, setRepeaterForm] = useState({
    name: "", latitude: "", longitude: "", frequency: "", inputFrequency: "",
    txTone: "", rxTone: "", mode: "FM", bandwidth: "25 kHz",
    coverageRadiusMiles: "6.2", heightFeet: "", notes: "", callsign: "",
  });
  const [repeaterSaving, setRepeaterSaving] = useState(false);
  const [repeaterAlert, setRepeaterAlert] = useState(null);
  const [showCoverage, setShowCoverage] = useState(true);

  // Regional Frequencies state
  const [regFreqs, setRegFreqs] = useState({});
  const [regFreqsLoading, setRegFreqsLoading] = useState(false);
  const [regFreqDialogOpen, setRegFreqDialogOpen] = useState(false);
  const [regFreqDialogMode, setRegFreqDialogMode] = useState("create");
  const [editRegFreq, setEditRegFreq] = useState(null);
  const [regFreqForm, setRegFreqForm] = useState({
    region: "", channelName: "", frequency: "",
    txTone: "", rxTone: "", mode: "FM", bandwidth: "25 kHz",
    assignment: "", notes: "", sortOrder: "0",
  });
  const [regFreqSaving, setRegFreqSaving] = useState(false);
  const [regFreqAlert, setRegFreqAlert] = useState(null);

  // Fetch patrol events
  const fetchPatrolEvents = async () => {
    setPatrolEventsLoading(true);
    try {
      const params = new URLSearchParams();
      if (showPastEvents) params.append("includePast", "true");
      const res = await api.get(`/patrol-events?${params.toString()}`);
      setPatrolEvents(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load patrol events:", err);
      setPatrolCalendarAlert({ severity: "error", message: "Failed to load events" });
    } finally {
      setPatrolEventsLoading(false);
    }
  };

  // Fetch dynamic resources from API
  const fetchResources = async () => {
    setResourcesLoading(true);
    try {
      const res = await api.get("/resources");
      const allResources = Array.isArray(res.data) ? res.data : [];
      
      // Separate by type
      setDynamicQuickLinks(allResources.filter(r => r.type === "quick-link"));
      setDynamicPolicies(allResources.filter(r => r.type === "policy"));
      
      // Parse ICS-205 data from JSON content
      const ics205Resource = allResources.find(r => r.type === "ics-205");
      if (ics205Resource && ics205Resource.content) {
        try {
          const parsedIcs205 = JSON.parse(ics205Resource.content);
          setDynamicIcs205Data(parsedIcs205);
        } catch (e) {
          console.error("Failed to parse ICS-205 data:", e);
        }
      }
    } catch (err) {
      console.error("Failed to load resources:", err);
      // Fall back to hardcoded values on error
    } finally {
      setResourcesLoading(false);
    }
  };

  // Fetch resources on component mount
  useEffect(() => {
    fetchResources();
  }, []);

  // Fetch users when Region Map tab is active
  useEffect(() => {
    if (activeTab === "region-map" && allUsers.length === 0) {
      setUsersLoading(true);
      api.get("/profiles/all")
        .then((res) => {
          const users = Array.isArray(res.data) ? res.data : [];
          setAllUsers(users);
        })
        .catch((err) => {
          console.error("Failed to load users:", err);
        })
        .finally(() => {
          setUsersLoading(false);
        });
    }
  }, [activeTab, allUsers.length]);

  // Fetch patrol events when Patrol Calendar tab is active
  useEffect(() => {
    if (activeTab === "patrol-calendar") {
      fetchPatrolEvents();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, showPastEvents]);

  // Fetch repeaters when tab is active
  const fetchRepeaters = useCallback(async () => {
    if (!canViewComms) return;
    setRepeatersLoading(true);
    try {
      const res = await api.get("/radio-repeaters");
      setRepeaters(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load repeaters:", err);
    } finally {
      setRepeatersLoading(false);
    }
  }, [canViewComms]);

  const fetchRegionalFrequencies = useCallback(async () => {
    if (!canViewComms) return;
    setRegFreqsLoading(true);
    try {
      const res = await api.get("/regional-frequencies/by-region");
      setRegFreqs(res.data && typeof res.data === "object" ? res.data : {});
    } catch (err) {
      console.error("Failed to load regional frequencies:", err);
    } finally {
      setRegFreqsLoading(false);
    }
  }, [canViewComms]);

  useEffect(() => {
    if (activeTab === "repeater-map") fetchRepeaters();
  }, [activeTab, fetchRepeaters]);

  useEffect(() => {
    if (activeTab === "regional-frequencies") fetchRegionalFrequencies();
  }, [activeTab, fetchRegionalFrequencies]);

  const handleTabChange = (_event, newValue) => {
    setActiveTab(newValue);
  };

  // Patrol Calendar handlers
  const handleOpenCreateEvent = () => {
    setEventDialogMode("create");
    setEventForm({
      title: "",
      description: "",
      eventType: "Other",
      imageUrl: "",
      location: "",
      locationTitle: "",
      latitude: "",
      longitude: "",
      region: "",
      radioChannel: "",
      startTimeUtc: "",
      endTimeUtc: "",
    });
    setEventDialogOpen(true);
  };

  const handleOpenEditEvent = (event) => {
    setEventDialogMode("edit");
    setSelectedEvent(event);
    setEventForm({
      title: event.title,
      description: event.description || "",
      eventType: event.eventType || "Other",
      imageUrl: event.imageUrl || "",
      location: event.location || "",
      locationTitle: event.locationTitle || "",
      latitude: event.latitude || "",
      longitude: event.longitude || "",
      region: event.region || "",
      radioChannel: event.radioChannel || "",
      startTimeUtc: formatDateTimeForInput(event.startTimeUtc),
      endTimeUtc: formatDateTimeForInput(event.endTimeUtc),
    });
    setEventDialogOpen(true);
  };

  const handleCloseEventDialog = () => {
    setEventDialogOpen(false);
    setSelectedEvent(null);
    setEventForm({
      title: "",
      description: "",
      eventType: "Other",
      imageUrl: "",
      location: "",
      locationTitle: "",
      latitude: "",
      longitude: "",
      region: "",
      radioChannel: "",
      startTimeUtc: "",
      endTimeUtc: "",
    });
  };

  // Helper to convert CST input to UTC ISO string
  const cstToUtcIso = (localDateTimeString) => {
    if (!localDateTimeString) return null;
    // Parse the datetime-local value and treat it as CST
    // datetime-local gives us "2026-01-24T10:00"
    // We need to convert CST to UTC (add 6 hours, or 5 during DST)
    const date = new Date(localDateTimeString);
    // Get the offset for CST/CDT at this date
    const cstDate = new Date(date.toLocaleString("en-US", { timeZone: "America/Chicago" }));
    const utcDate = new Date(date.toLocaleString("en-US", { timeZone: "UTC" }));
    const offsetMs = utcDate - cstDate;
    // The datetime-local is in user's local time, but we want CST
    // Since user is in CST, their local time IS CST, so just convert to ISO
    return date.toISOString();
  };

  // Helper to convert UTC ISO string to CST for datetime-local input
  const utcToCstInput = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    // Format in CST timezone for the input
    const options = {
      timeZone: "America/Chicago",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    };
    const parts = new Intl.DateTimeFormat("en-CA", options).formatToParts(date);
    const get = (type) => parts.find(p => p.type === type)?.value || "";
    return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
  };

  const formatDateTimeForInput = (isoString) => {
    return utcToCstInput(isoString);
  };

  const handleEventFormChange = (field) => (event) => {
    setEventForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  // Handle image file upload
  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      setPatrolCalendarAlert({ severity: "error", message: "Invalid file type. Allowed: jpg, png, gif, webp" });
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setPatrolCalendarAlert({ severity: "error", message: "File too large. Maximum size is 10MB" });
      return;
    }

    setImageUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await api.post("/patrol-events/upload-image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setEventForm((prev) => ({ ...prev, imageUrl: res.data.url }));
      setPatrolCalendarAlert({ severity: "success", message: "Image uploaded successfully" });
    } catch (err) {
      console.error("Failed to upload image:", err);
      setPatrolCalendarAlert({ 
        severity: "error", 
        message: err.response?.data?.message || "Failed to upload image" 
      });
    } finally {
      setImageUploading(false);
    }
  };

  // Geocode address to get coordinates
  const handleGeocodeAddress = async () => {
    if (!eventForm.location.trim()) {
      setPatrolCalendarAlert({ severity: "warning", message: "Please enter an address first" });
      return;
    }
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(eventForm.location)}&limit=1`
      );
      const data = await response.json();
      if (data && data.length > 0) {
        setEventForm((prev) => ({
          ...prev,
          latitude: parseFloat(data[0].lat),
          longitude: parseFloat(data[0].lon),
        }));
        setPatrolCalendarAlert({ severity: "success", message: "Location found and coordinates set" });
      } else {
        setPatrolCalendarAlert({ severity: "warning", message: "Could not find coordinates for this address" });
      }
    } catch (err) {
      console.error("Geocoding error:", err);
      setPatrolCalendarAlert({ severity: "error", message: "Failed to geocode address" });
    }
  };

  const handleSaveEvent = async () => {
    if (!eventForm.title.trim()) {
      setPatrolCalendarAlert({ severity: "error", message: "Title is required" });
      return;
    }
    if (!eventForm.startTimeUtc || !eventForm.endTimeUtc) {
      setPatrolCalendarAlert({ severity: "error", message: "Start and end times are required" });
      return;
    }

    const payload = {
      title: eventForm.title.trim(),
      description: eventForm.description.trim() || null,
      eventType: eventForm.eventType || "Other",
      imageUrl: eventForm.imageUrl.trim() || null,
      location: eventForm.location.trim() || null,
      locationTitle: eventForm.locationTitle.trim() || null,
      latitude: eventForm.latitude ? parseFloat(eventForm.latitude) : null,
      longitude: eventForm.longitude ? parseFloat(eventForm.longitude) : null,
      region: eventForm.region || null,
      radioChannel: eventForm.radioChannel.trim() || null,
      startTimeUtc: new Date(eventForm.startTimeUtc).toISOString(),
      endTimeUtc: new Date(eventForm.endTimeUtc).toISOString(),
    };

    try {
      if (eventDialogMode === "create") {
        await api.post("/patrol-events", payload);
        setPatrolCalendarAlert({ severity: "success", message: "Event created successfully" });
      } else {
        await api.put(`/patrol-events/${selectedEvent.id}`, {
          ...payload,
          isCancelled: selectedEvent.isCancelled,
        });
        setPatrolCalendarAlert({ severity: "success", message: "Event updated successfully" });
      }
      handleCloseEventDialog();
      fetchPatrolEvents();
    } catch (err) {
      console.error("Failed to save event:", err);
      setPatrolCalendarAlert({ 
        severity: "error", 
        message: err.response?.data || "Failed to save event" 
      });
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm("Are you sure you want to delete this event?")) return;
    try {
      await api.delete(`/patrol-events/${eventId}`);
      setPatrolCalendarAlert({ severity: "success", message: "Event deleted" });
      fetchPatrolEvents();
    } catch (err) {
      console.error("Failed to delete event:", err);
      setPatrolCalendarAlert({ severity: "error", message: "Failed to delete event" });
    }
  };

  const handleCancelEvent = async (event) => {
    try {
      await api.put(`/patrol-events/${event.id}`, {
        title: event.title,
        description: event.description,
        location: event.location,
        startTimeUtc: event.startTimeUtc,
        endTimeUtc: event.endTimeUtc,
        isCancelled: !event.isCancelled,
      });
      setPatrolCalendarAlert({ 
        severity: "success", 
        message: event.isCancelled ? "Event restored" : "Event cancelled" 
      });
      fetchPatrolEvents();
    } catch (err) {
      console.error("Failed to toggle event cancellation:", err);
      setPatrolCalendarAlert({ severity: "error", message: "Failed to update event" });
    }
  };

  const handleSelfSignup = async (eventId) => {
    try {
      await api.post(`/patrol-events/${eventId}/signup`, { notes: null });
      setPatrolCalendarAlert({ severity: "success", message: "You're signed up!" });
      fetchPatrolEvents();
    } catch (err) {
      console.error("Failed to sign up:", err);
      setPatrolCalendarAlert({ 
        severity: "error", 
        message: err.response?.data || "Failed to sign up" 
      });
    }
  };

  const handleCancelOwnSignup = async (eventId) => {
    try {
      await api.delete(`/patrol-events/${eventId}/signup`);
      setPatrolCalendarAlert({ severity: "info", message: "Signup cancelled" });
      fetchPatrolEvents();
    } catch (err) {
      console.error("Failed to cancel signup:", err);
      setPatrolCalendarAlert({ severity: "error", message: "Failed to cancel signup" });
    }
  };

  const handleOpenSignupDialog = async (event) => {
    setSignupTargetEvent(event);
    setSelectedUserForSignup(null);
    setSignupNotes("");
    try {
      const res = await api.get("/patrol-events/users");
      // Filter out users already signed up
      const signedUpUserIds = event.signups.map(s => s.userId);
      const available = (res.data || []).filter(u => !signedUpUserIds.includes(u.id));
      setAvailableUsersForSignup(available);
    } catch (err) {
      console.error("Failed to load users:", err);
      setAvailableUsersForSignup([]);
    }
    setSignupDialogOpen(true);
  };

  const handleCloseSignupDialog = () => {
    setSignupDialogOpen(false);
    setSignupTargetEvent(null);
    setSelectedUserForSignup(null);
    setSignupNotes("");
  };

  const handleSupervisorSignup = async () => {
    if (!selectedUserForSignup) {
      setPatrolCalendarAlert({ severity: "error", message: "Please select a user" });
      return;
    }
    try {
      await api.post(`/patrol-events/${signupTargetEvent.id}/signup`, {
        userId: selectedUserForSignup.id,
        notes: signupNotes.trim() || null,
      });
      setPatrolCalendarAlert({ severity: "success", message: `${selectedUserForSignup.name || selectedUserForSignup.username} signed up` });
      handleCloseSignupDialog();
      fetchPatrolEvents();
    } catch (err) {
      console.error("Failed to sign up user:", err);
      setPatrolCalendarAlert({ 
        severity: "error", 
        message: err.response?.data || "Failed to sign up user" 
      });
    }
  };

  const handleRemoveUserFromEvent = async (eventId, userId) => {
    try {
      await api.delete(`/patrol-events/${eventId}/signup?userId=${userId}`);
      setPatrolCalendarAlert({ severity: "info", message: "User removed from event" });
      fetchPatrolEvents();
    } catch (err) {
      console.error("Failed to remove user:", err);
      setPatrolCalendarAlert({ severity: "error", message: "Failed to remove user" });
    }
  };

  const isUserSignedUp = (event) => {
    return event.signups?.some(s => s.userId === user?.id);
  };

  const formatEventDateTime = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    return date.toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZone: "America/Chicago",
    });
  };

  const handleExpenseChange = (field) => (event) => {
    setExpenseForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    setExpenseError(null);
  };

  const handleExpenseReceiptChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp", "application/pdf"];
      if (!allowedTypes.includes(file.type)) {
        setExpenseError("Invalid file type. Allowed: jpg, png, gif, webp, pdf");
        return;
      }
      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        setExpenseError("File size exceeds 10MB limit.");
        return;
      }
      setExpenseReceipt(file);
      setExpenseError(null);
    }
  };

  const handleSetTodayDate = () => {
    setExpenseForm((prev) => ({
      ...prev,
      purchaseDate: new Date().toISOString().split("T")[0],
    }));
  };

  const handleExpenseSubmit = async (event) => {
    event.preventDefault();
    setExpenseError(null);
    setExpenseSubmitting(true);

    try {
      // Validate required fields
      if (!expenseForm.purchaseDate || !expenseForm.category || !expenseForm.itemDescription || !expenseForm.amountDollars) {
        setExpenseError("Please fill in all required fields.");
        setExpenseSubmitting(false);
        return;
      }

      // Validate notes for "Other" category
      const selectedCategory = EXPENSE_CATEGORIES.find(c => c.id === parseInt(expenseForm.category));
      if (selectedCategory?.requiresNotes && !expenseForm.notes) {
        setExpenseError("Notes are required for 'Other / Miscellaneous' category.");
        setExpenseSubmitting(false);
        return;
      }

      // Convert dollars to cents
      const amountCents = Math.round(parseFloat(expenseForm.amountDollars) * 100);
      if (isNaN(amountCents) || amountCents <= 0) {
        setExpenseError("Please enter a valid amount.");
        setExpenseSubmitting(false);
        return;
      }

      // Create expense
      const expenseData = {
        purchaseDate: expenseForm.purchaseDate,
        category: parseInt(expenseForm.category),
        itemDescription: expenseForm.itemDescription,
        amountCents,
        notes: expenseForm.notes || null,
      };

      const response = await api.post("/expenses", expenseData);
      const createdExpense = response.data;

      // Upload receipt if provided
      if (expenseReceipt && createdExpense.id) {
        const formData = new FormData();
        formData.append("file", expenseReceipt);
        try {
          await api.post(`/expenses/${createdExpense.id}/receipt`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (receiptError) {
          console.error("Receipt upload failed:", receiptError);
          // Don't fail the whole submission, expense was created
        }
      }

      setExpenseSubmitted(true);
      setExpenseForm({
        purchaseDate: new Date().toISOString().split("T")[0],
        category: "",
        itemDescription: "",
        amountDollars: "",
        notes: "",
      });
      setExpenseReceipt(null);
      // Clear file input
      const fileInput = document.getElementById("expense-receipt-input");
      if (fileInput) fileInput.value = "";

      setTimeout(() => setExpenseSubmitted(false), 5000);
    } catch (error) {
      console.error("Expense submission error:", error);
      setExpenseError(error.response?.data || "Failed to submit expense. Please try again.");
    } finally {
      setExpenseSubmitting(false);
    }
  };

  const handleOpenPolicy = (policy) => {
    setSelectedPolicy(policy);
    setPolicyDialogOpen(true);
  };

  const handleClosePolicy = () => {
    setPolicyDialogOpen(false);
    setSelectedPolicy(null);
  };

  // Simple markdown-like renderer for policy content
  const renderPolicyContent = (content) => {
    if (!content) return null;
    
    // Helper to parse inline formatting (bold, italic)
    const parseInlineFormatting = (text) => {
      const parts = [];
      let remaining = text;
      let key = 0;
      
      // Match **bold** and *italic* patterns
      const regex = /(\*\*([^*]+)\*\*|\*([^*]+)\*)/g;
      let lastIndex = 0;
      let match;
      
      while ((match = regex.exec(text)) !== null) {
        // Add text before the match
        if (match.index > lastIndex) {
          parts.push(text.substring(lastIndex, match.index));
        }
        
        // Add formatted text
        if (match[2]) {
          // Bold text (**)
          parts.push(<strong key={key++}>{match[2]}</strong>);
        } else if (match[3]) {
          // Italic text (*)
          parts.push(<em key={key++}>{match[3]}</em>);
        }
        
        lastIndex = regex.lastIndex;
      }
      
      // Add remaining text
      if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex));
      }
      
      return parts.length > 0 ? parts : text;
    };
    
    const lines = content.split('\n');
    const elements = [];
    let listItems = [];
    let listType = 'ul'; // 'ul' or 'ol'

    const flushList = () => {
      if (listItems.length > 0) {
        const ListComponent = listType === 'ol' ? 'ol' : 'ul';
        elements.push(
          <Box 
            component={ListComponent} 
            key={`list-${elements.length}`} 
            sx={{ 
              pl: 3, 
              my: 1.5,
              '& li': {
                mb: 0.75,
              }
            }}
          >
            {listItems.map((item, idx) => (
              <Typography component="li" key={idx} variant="body2" sx={{ lineHeight: 1.6 }}>
                {item}
              </Typography>
            ))}
          </Box>
        );
        listItems = [];
      }
    };

    lines.forEach((line, index) => {
      const trimmed = line.trim();
      
      if (!trimmed) {
        flushList();
        return;
      }

      // H1 headers
      if (trimmed.startsWith('# ') && !trimmed.startsWith('## ')) {
        flushList();
        elements.push(
          <Typography
            key={index}
            variant="h5"
            sx={{ fontWeight: 700, mt: 3, mb: 2, textTransform: "uppercase", letterSpacing: 1, borderBottom: '2px solid', borderColor: 'primary.main', pb: 1 }}
          >
            {parseInlineFormatting(trimmed.replace('# ', ''))}
          </Typography>
        );
        return;
      }

      // H2 headers
      if (trimmed.startsWith('## ')) {
        flushList();
        elements.push(
          <Typography
            key={index}
            variant="h6"
            sx={{ fontWeight: 700, mt: 3, mb: 1, textTransform: "uppercase", letterSpacing: 0.5 }}
          >
            {parseInlineFormatting(trimmed.replace('## ', ''))}
          </Typography>
        );
        return;
      }

      // H3 headers
      if (trimmed.startsWith('### ')) {
        flushList();
        elements.push(
          <Typography
            key={index}
            variant="subtitle1"
            sx={{ fontWeight: 700, mt: 2, mb: 1 }}
          >
            {parseInlineFormatting(trimmed.replace('### ', ''))}
          </Typography>
        );
        return;
      }

      // H4 headers
      if (trimmed.startsWith('#### ')) {
        flushList();
        elements.push(
          <Typography
            key={index}
            variant="subtitle2"
            sx={{ fontWeight: 600, mt: 1.5, mb: 0.5 }}
          >
            {parseInlineFormatting(trimmed.replace('#### ', ''))}
          </Typography>
        );
        return;
      }

      // Bullet list items (- or *)
      if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        listType = 'ul';
        const text = trimmed.substring(2);
        listItems.push(parseInlineFormatting(text));
        return;
      }

      // Numbered list items (1. 2. etc)
      if (/^\d+\.\s/.test(trimmed)) {
        listType = 'ol';
        const text = trimmed.replace(/^\d+\.\s/, '');
        listItems.push(parseInlineFormatting(text));
        return;
      }

      // Horizontal rule
      if (trimmed === '---' || trimmed === '***' || trimmed === '___') {
        flushList();
        elements.push(
          <Box key={index} sx={{ my: 2, borderTop: '1px solid', borderColor: 'divider' }} />
        );
        return;
      }

      // Blockquote
      if (trimmed.startsWith('> ')) {
        flushList();
        elements.push(
          <Box
            key={index}
            sx={{
              borderLeft: '4px solid',
              borderColor: 'primary.main',
              pl: 2,
              py: 1,
              my: 1.5,
              backgroundColor: 'action.hover',
              borderRadius: '0 4px 4px 0',
            }}
          >
            <Typography variant="body2" sx={{ fontStyle: 'italic', lineHeight: 1.7 }}>
              {parseInlineFormatting(trimmed.replace('> ', ''))}
            </Typography>
          </Box>
        );
        return;
      }

      // Regular paragraph
      flushList();
      elements.push(
        <Typography key={index} variant="body2" sx={{ mb: 1.5, lineHeight: 1.8 }}>
          {parseInlineFormatting(trimmed)}
        </Typography>
      );
    });

    flushList();
    return elements;
  };

  const renderTxdotCams = () => (
    <Box sx={{ width: "100%", height: "calc(100vh - 280px)", minHeight: 500 }}>
      <iframe
        src="https://its.txdot.gov/its/District/DAL"
        title="TXDOT Traffic Cameras - Dallas District"
        style={{
          width: "100%",
          height: "100%",
          border: "none",
          borderRadius: 8,
        }}
        allow="fullscreen"
      />
    </Box>
  );

  // ICS-205 Cell Style
  const icsCell = {
    border: "1px solid #000",
    p: 0.75,
    fontSize: "0.75rem",
    color: "#000",
    backgroundColor: "#fff",
    "@media print": {
      fontSize: "9pt",
      p: 0.5,
    },
  };

  const icsHeaderCell = {
    ...icsCell,
    fontWeight: 700,
    backgroundColor: "#e0e0e0",
    color: "#000",
    textTransform: "uppercase",
    textAlign: "center",
    "@media print": {
      backgroundColor: "#e0e0e0 !important",
      WebkitPrintColorAdjust: "exact",
      printColorAdjust: "exact",
    },
  };

  const icsLabelCell = {
    ...icsCell,
    fontWeight: 600,
    backgroundColor: "#f5f5f5",
    color: "#000",
    width: "150px",
    "@media print": {
      backgroundColor: "#f5f5f5 !important",
      WebkitPrintColorAdjust: "exact",
      printColorAdjust: "exact",
    },
  };

  const renderICS205 = () => {
    // Use dynamic data if available, otherwise fall back to default
    const ICS_205_DATA = dynamicIcs205Data || DEFAULT_ICS_205_DATA;
    
    return (
    <Box 
      id="ics-205-form"
      sx={{ 
        backgroundColor: "#fff", 
        color: "#000",
        p: 2,
        "@media print": {
          p: 0,
          "& *": {
            color: "#000 !important",
          },
        },
      }}
    >
      {/* Print Button */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2, "@media print": { display: "none" } }}>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={() => window.print()}
          sx={{ textTransform: "uppercase", fontWeight: 600 }}
        >
          Print ICS-205
        </Button>
      </Box>

      {/* Main Form Container */}
      <Box sx={{ border: "2px solid #000", backgroundColor: "#fff" }}>
        {/* Header */}
        <Box sx={{ display: "flex", borderBottom: "2px solid #000" }}>
          <Box sx={{ 
            flex: 1, 
            p: 1, 
            borderRight: "1px solid #000",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}>
            <Typography sx={{ fontSize: "1.25rem", fontWeight: 700, textAlign: "center" }}>
              INCIDENT RADIO COMMUNICATIONS PLAN
            </Typography>
            <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, textAlign: "center", color: "#666" }}>
              ICS 205
            </Typography>
          </Box>
        </Box>

        {/* Incident Info Row */}
        <Box sx={{ display: "flex", borderBottom: "1px solid #000" }}>
          <Box sx={{ flex: 2, borderRight: "1px solid #000" }}>
            <Box sx={{ ...icsLabelCell, borderBottom: "none", borderLeft: "none", borderTop: "none" }}>
              1. Incident Name
            </Box>
            <Box sx={{ ...icsCell, borderBottom: "none", borderLeft: "none", borderTop: "none", fontWeight: 600, fontSize: "0.875rem" }}>
              {ICS_205_DATA.incidentName}
            </Box>
          </Box>
          <Box sx={{ flex: 1, borderRight: "1px solid #000" }}>
            <Box sx={{ ...icsLabelCell, borderBottom: "none", borderLeft: "none", borderTop: "none" }}>
              2. Date/Time Prepared
            </Box>
            <Box sx={{ ...icsCell, borderBottom: "none", borderLeft: "none", borderTop: "none" }}>
              Date: {ICS_205_DATA.preparedBy?.date || new Date().toLocaleDateString()}<br />
              Time: {ICS_205_DATA.preparedBy?.time || new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </Box>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ ...icsLabelCell, borderBottom: "none", borderLeft: "none", borderTop: "none", borderRight: "none" }}>
              3. Operational Period
            </Box>
            <Box sx={{ ...icsCell, borderBottom: "none", borderLeft: "none", borderTop: "none", borderRight: "none" }}>
              Date: {ICS_205_DATA.operationalPeriod?.dateFrom}<br />
              Time: {ICS_205_DATA.operationalPeriod?.timeFrom} - {ICS_205_DATA.operationalPeriod?.timeTo}
            </Box>
          </Box>
        </Box>

        {/* Section 4: Basic Radio Channel Use */}
        <Box sx={{ borderBottom: "1px solid #000" }}>
          <Box sx={{ ...icsLabelCell, borderLeft: "none", borderRight: "none", borderTop: "none" }}>
            4. Basic Radio Channel Use
          </Box>
        </Box>

        {/* Radio Channel Table */}
        <Table size="small" sx={{ "& td, & th": { ...icsCell, color: "#000" } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={icsHeaderCell}>Zone/Group</TableCell>
              <TableCell sx={icsHeaderCell}>Channel</TableCell>
              <TableCell sx={icsHeaderCell}>Function</TableCell>
              <TableCell sx={icsHeaderCell}>Assignment</TableCell>
              <TableCell sx={icsHeaderCell}>RX Freq</TableCell>
              <TableCell sx={icsHeaderCell}>RX Tone/NAC</TableCell>
              <TableCell sx={icsHeaderCell}>TX Freq</TableCell>
              <TableCell sx={icsHeaderCell}>TX Tone/NAC</TableCell>
              <TableCell sx={icsHeaderCell}>Mode (A/D/M)</TableCell>
              <TableCell sx={icsHeaderCell}>Remarks</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(ICS_205_DATA.basicRadioChannelUse || []).map((channel, index) => (
              <TableRow key={index}>
                <TableCell sx={icsCell}>{channel.zone}</TableCell>
                <TableCell sx={{ ...icsCell, fontWeight: 600 }}>{channel.channel}</TableCell>
                <TableCell sx={icsCell}>{channel.function}</TableCell>
                <TableCell sx={icsCell}>{channel.assignment}</TableCell>
                <TableCell sx={{ ...icsCell, fontFamily: "monospace" }}>{channel.rxFreq}</TableCell>
                <TableCell sx={{ ...icsCell, fontFamily: "monospace" }}>{channel.rxTone}</TableCell>
                <TableCell sx={{ ...icsCell, fontFamily: "monospace" }}>{channel.txFreq}</TableCell>
                <TableCell sx={{ ...icsCell, fontFamily: "monospace" }}>{channel.txTone}</TableCell>
                <TableCell sx={{ ...icsCell, textAlign: "center" }}>{channel.mode}</TableCell>
                <TableCell sx={icsCell}>{channel.remarks}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Section 5: Special Instructions */}
        <Box sx={{ borderTop: "1px solid #000" }}>
          <Box sx={{ ...icsLabelCell, borderLeft: "none", borderRight: "none", borderBottom: "none" }}>
            5. Special Instructions
          </Box>
          <Box sx={{ p: 1.5, borderTop: "1px solid #000", backgroundColor: "#fff" }}>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {(ICS_205_DATA.specialInstructions || []).map((instruction, index) => (
                <Typography 
                  component="li" 
                  key={index} 
                  sx={{ fontSize: "0.8rem", mb: 0.5, lineHeight: 1.4, color: "#000" }}
                >
                  {instruction}
                </Typography>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Section 6: Prepared By */}
        <Box sx={{ display: "flex", borderTop: "2px solid #000" }}>
          <Box sx={{ flex: 1, borderRight: "1px solid #000", backgroundColor: "#fff" }}>
            <Box sx={{ ...icsLabelCell, borderLeft: "none", borderTop: "none", borderBottom: "none" }}>
              6. Prepared By (Communications Unit Leader)
            </Box>
            <Box sx={{ p: 1 }}>
              <Typography sx={{ fontSize: "0.8rem", color: "#000" }}>
                <strong>Name:</strong> {ICS_205_DATA.preparedBy?.name}
              </Typography>
              <Typography sx={{ fontSize: "0.8rem", color: "#000" }}>
                <strong>Position:</strong> {ICS_205_DATA.preparedBy?.position}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ flex: 1, backgroundColor: "#fff" }}>
            <Box sx={{ ...icsLabelCell, borderRight: "none", borderTop: "none", borderBottom: "none" }}>
              Date/Time
            </Box>
            <Box sx={{ p: 1 }}>
              <Typography sx={{ fontSize: "0.8rem", color: "#000" }}>
                {ICS_205_DATA.preparedBy?.date || new Date().toLocaleDateString()} / {ICS_205_DATA.preparedBy?.time || new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ borderTop: "1px solid #000", p: 0.5, backgroundColor: "#f5f5f5" }}>
          <Typography sx={{ fontSize: "0.65rem", textAlign: "center", color: "#666" }}>
            ICS 205 | Incident Radio Communications Plan | Page 1 of 1
          </Typography>
        </Box>
      </Box>

      {/* Mode Legend */}
      <Box sx={{ mt: 2, p: 1.5, backgroundColor: "#f9f9f9", borderRadius: 1, border: "1px solid #ddd", "@media print": { mt: 1 } }}>
        <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, mb: 0.5 }}>
          MODE LEGEND:
        </Typography>
        <Typography sx={{ fontSize: "0.7rem", color: "#666" }}>
          <strong>A</strong> = Analog | <strong>D</strong> = Digital | <strong>M</strong> = Mixed Mode | 
          <strong> PL</strong> = CTCSS Tone | <strong>CSQ</strong> = Carrier Squelch (No Tone)
        </Typography>
      </Box>
    </Box>
  );
  };

  const renderPolicies = () => {
    // Use dynamic data if available, otherwise fall back to hardcoded
    // Convert dynamic policies to match expected format
    const policies = dynamicPolicies.length > 0 
      ? dynamicPolicies.map(p => ({
          id: p.id,
          title: p.title,
          description: p.description,
          content: p.content,
          lastUpdated: p.lastUpdated ? new Date(p.lastUpdated).toLocaleDateString() : 'N/A',
        }))
      : POLICY_DOCUMENTS;
    
    return (
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Access official policies and procedures. Click "View" to read the full document.
        </Typography>
        {resourcesLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, textTransform: "uppercase" }}>Document</TableCell>
                <TableCell sx={{ fontWeight: 700, textTransform: "uppercase" }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 700, textTransform: "uppercase" }}>Last Updated</TableCell>
                <TableCell sx={{ fontWeight: 700, textTransform: "uppercase" }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {policies.map((doc) => (
                <TableRow key={doc.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {doc.title}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {doc.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {doc.lastUpdated}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleOpenPolicy(doc)}
                      sx={{ textTransform: "uppercase", fontWeight: 600 }}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>
    );
  };

  const renderLogExpense = () => {
    const selectedCategory = EXPENSE_CATEGORIES.find(c => c.id === parseInt(expenseForm.category));
    
    return (
    <Box sx={{ maxWidth: 700 }}>
      <Alert severity="warning" sx={{ mb: 3 }}>
        <Typography variant="body2" fontWeight={600}>
          This form is for transparency only. Texas Highway Rangers does not provide reimbursement for submitted expenses.
        </Typography>
      </Alert>

      {expenseSubmitted && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Expense logged successfully! Thank you for your transparency.
        </Alert>
      )}

      {expenseError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {expenseError}
        </Alert>
      )}

      <form onSubmit={handleExpenseSubmit}>
        <Stack spacing={3}>
          {/* Date of Purchase */}
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
            <TextField
              label="Date of Purchase *"
              type="date"
              value={expenseForm.purchaseDate}
              onChange={handleExpenseChange("purchaseDate")}
              InputLabelProps={{ shrink: true }}
              required
              fullWidth
            />
            <Button
              variant="outlined"
              onClick={handleSetTodayDate}
              sx={{ 
                minWidth: 100, 
                height: 56, 
                textTransform: "uppercase", 
                fontWeight: 700 
              }}
            >
              Today
            </Button>
          </Box>

          {/* Expense Category */}
          <FormControl fullWidth required>
            <InputLabel>Expense Category *</InputLabel>
            <Select
              value={expenseForm.category}
              label="Expense Category *"
              onChange={handleExpenseChange("category")}
            >
              {EXPENSE_CATEGORIES.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {category.id}. {category.name}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Category Description */}
          {selectedCategory && (
            <Alert severity="info" icon={false} sx={{ py: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Category includes:</strong> {selectedCategory.description}
              </Typography>
            </Alert>
          )}

          {/* Item(s) Purchased */}
          <TextField
            label="Item(s) Purchased *"
            value={expenseForm.itemDescription}
            onChange={handleExpenseChange("itemDescription")}
            required
            fullWidth
            placeholder="e.g., 2x LED flares, 1x jump pack cable"
            inputProps={{ maxLength: 512 }}
          />

          {/* Total Amount Paid */}
          <TextField
            label="Total Amount Paid ($) *"
            type="number"
            value={expenseForm.amountDollars}
            onChange={handleExpenseChange("amountDollars")}
            required
            fullWidth
            inputProps={{ min: 0.01, step: 0.01 }}
            placeholder="0.00"
            InputProps={{
              startAdornment: <Typography sx={{ mr: 1, color: "text.secondary" }}>$</Typography>,
            }}
          />

          {/* Notes (required for "Other" category) */}
          <TextField
            label={selectedCategory?.requiresNotes ? "Notes * (required for this category)" : "Notes (optional)"}
            value={expenseForm.notes}
            onChange={handleExpenseChange("notes")}
            required={selectedCategory?.requiresNotes}
            fullWidth
            multiline
            rows={3}
            placeholder={selectedCategory?.requiresNotes 
              ? "Please describe this expense since it doesn't fit other categories..." 
              : "Any additional notes about this expense..."}
            inputProps={{ maxLength: 1024 }}
          />

          {/* Receipt Upload */}
          <Box>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
              Receipt Upload (optional)
            </Typography>
            <input
              id="expense-receipt-input"
              type="file"
              accept="image/*,.pdf"
              onChange={handleExpenseReceiptChange}
              style={{ display: "none" }}
            />
            <label htmlFor="expense-receipt-input">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUploadIcon />}
                sx={{ textTransform: "uppercase", fontWeight: 600 }}
              >
                {expenseReceipt ? "Change Receipt" : "Upload Receipt"}
              </Button>
            </label>
            {expenseReceipt && (
              <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                <CheckCircleIcon color="success" fontSize="small" />
                <Typography variant="body2" color="text.secondary">
                  {expenseReceipt.name} ({(expenseReceipt.size / 1024).toFixed(1)} KB)
                </Typography>
                <IconButton 
                  size="small" 
                  onClick={() => {
                    setExpenseReceipt(null);
                    const fileInput = document.getElementById("expense-receipt-input");
                    if (fileInput) fileInput.value = "";
                  }}
                >
                  <CancelIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              Accepted formats: JPG, PNG, GIF, WebP, PDF (max 10MB)
            </Typography>
          </Box>

          {/* Submit Button */}
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            disabled={expenseSubmitting}
            sx={{ textTransform: "uppercase", fontWeight: 700 }}
          >
            {expenseSubmitting ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                Submitting...
              </>
            ) : (
              "Log Expense"
            )}
          </Button>
        </Stack>
      </form>
    </Box>
  );
  };

  const renderQuickLinks = () => {
    const renderLinkTable = (links) => (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, textTransform: "uppercase" }}>Resource</TableCell>
            <TableCell sx={{ fontWeight: 700, textTransform: "uppercase" }}>Description</TableCell>
            <TableCell sx={{ fontWeight: 700, textTransform: "uppercase" }}>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {links.map((link, index) => (
            <TableRow key={link.id || index} hover>
              <TableCell>
                <Typography variant="body2" fontWeight={600}>
                  {link.title}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {link.description}
                </Typography>
              </TableCell>
              <TableCell>
                <Button
                  variant="outlined"
                  size="small"
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ textTransform: "uppercase", fontWeight: 600 }}
                >
                  Open
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );

    // Build categorized link lists from dynamic API data, falling back to hardcoded defaults
    const dynamicCommon = dynamicQuickLinks.filter(l => l.category === "common" || !l.category);
    const dynamicTexas = dynamicQuickLinks.filter(l => l.category === "texas");
    const dynamicSC = dynamicQuickLinks.filter(l => l.category === "south-carolina");

    // Use dynamic links if any exist in that category, otherwise fall back to hardcoded
    const commonLinks = dynamicCommon.length > 0 ? dynamicCommon : QUICK_LINKS_COMMON;
    const texasLinks = dynamicTexas.length > 0 ? dynamicTexas : QUICK_LINKS_TEXAS;
    const scLinks = dynamicSC.length > 0 ? dynamicSC : QUICK_LINKS_SC;

    return (
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Helpful external resources and links for Highway Rangers.
        </Typography>
        {resourcesLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Stack spacing={4}>
            {/* Common Links */}
            <Box>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, mb: 1, display: "flex", alignItems: "center", gap: 1 }}
              >
                🌐 Common Resources
              </Typography>
              {renderLinkTable(commonLinks)}
            </Box>

            {/* State-Specific Links Side by Side */}
            <Grid container spacing={3} justifyContent="center">
              {/* Texas Links */}
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, mb: 1, display: "flex", alignItems: "center", gap: 1 }}
                  >
                    🤠 Texas
                  </Typography>
                  {renderLinkTable(texasLinks)}
                </Paper>
              </Grid>
              {/* South Carolina Links */}
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, mb: 1, display: "flex", alignItems: "center", gap: 1 }}
                  >
                    🌴 South Carolina
                  </Typography>
                  {renderLinkTable(scLinks)}
                </Paper>
              </Grid>
            </Grid>
          </Stack>
        )}
      </Box>
    );
  };

  const renderPrayer = () => (
    <Box sx={{ p: 3, maxWidth: 800, mx: "auto" }}>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: 1,
          mb: 3,
          textAlign: "center",
        }}
      >
        Why We Pray Before We Roll Out
      </Typography>

      <Stack spacing={3}>
        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
          Out here on the Texas roads, it's not just flat tires and stalled engines...it's real people, in real need.
        </Typography>

        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
          Faith keeps us grounded. It reminds us why we do what we do.
        </Typography>

        <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
          Before we roll out, we take a moment to pray; not just for safety, but for strength, wisdom, and the chance to be a light in someone's darkest moment.
        </Typography>

        <Box sx={{ my: 2, textAlign: "center" }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontStyle: "italic",
            }}
          >
            This isn't just volunteer work.
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontStyle: "italic",
            }}
          >
            It's a calling.
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Paper
          elevation={0}
          sx={{
            p: 4,
            backgroundColor: "action.hover",
            borderLeft: 4,
            borderColor: "primary.main",
            borderRadius: 1,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontStyle: "italic",
              lineHeight: 2,
              whiteSpace: "pre-line",
            }}
          >
{`"Lord above,
We ride out today to lend a hand where it's needed most.
Keep our eyes sharp, our grip steady, and our hearts true.
Watch over our travels down these long Texas roads.
When trouble comes, give us the courage to face it,
And the wisdom to know when to stand, and when to steer clear.
Bless the folks we stop to help,
And guide us all safe back to the home fires.
Amen."`}
          </Typography>
        </Paper>
      </Stack>
    </Box>
  );

  const renderAwards = () => {
    const categories = [
      { id: "certification", label: "Certifications", color: "primary" },
      { id: "recognition", label: "Recognition Awards", color: "warning" },
      { id: "achievement", label: "Achievement Badges", color: "success" },
      { id: "service", label: "Service Awards", color: "info" },
      { id: "gear", label: "Gear Collector", color: "secondary" },
    ];

    // Award criteria mapping - maps award IDs to the exact dispositions from SERVICE_OPTIONS
    const awardCriteria = [
      {
        awardId: "jumpstart-jedi",
        title: "Jumpstart Jedi",
        icon: "⚡",
        services: ["JUMP START"],
        required: 20,
        period: "365 days",
      },
      {
        awardId: "lug-nut-whisperer",
        title: "Lug Nut Whisperer",
        icon: "🔧",
        services: ["TIRE CHANGE"],
        required: 20,
        period: "365 days",
      },
      {
        awardId: "field-repair-wizard",
        title: "Field Repair Wizard",
        icon: "🧙",
        services: ["MINOR REPAIR"],
        required: 30,
        period: "365 days",
      },
      {
        awardId: "rope-wrangler",
        title: "Rope Wrangler",
        icon: "🪢",
        description: "Pull 10 vehicles",
        services: ["PUSH/PULL/TOW/RECOVERY"],
        required: 10,
        period: "365 days",
      },
      {
        awardId: "bumper-buddy",
        title: "Bumper Buddy",
        icon: "🚗",
        description: "Push 10 vehicles",
        services: ["PUSH/PULL/TOW/RECOVERY"],
        required: 10,
        period: "365 days",
      },
      {
        awardId: "recovery-agent",
        title: "Recovery Agent",
        icon: "🛻",
        description: "Recover 5 vehicles",
        services: ["PUSH/PULL/TOW/RECOVERY"],
        required: 5,
        period: "365 days",
      },
      {
        awardId: "cooler-command",
        title: "Cooler Command",
        icon: "💧",
        services: ["BOTTLED WATER/SNACK"],
        required: 10,
        period: "365 days",
      },
      {
        awardId: "target-acquired",
        title: "Target Acquired",
        icon: "🔭",
        services: ["GOA"],
        incidentBased: true,
        description: "First on scene, gone on arrival",
        required: 75,
        period: "365 days",
      },
      {
        awardId: "wrong-place-perfect-time",
        title: "Wrong Place, Perfect Time",
        icon: "🎯",
        services: ["ACCIDENT"],
        incidentBased: true,
        description: "First on scene of accidents",
        required: 10,
        period: "365 days",
      },
      {
        awardId: "fuel-fairy",
        title: "Fuel Fairy",
        icon: "🛢️",
        services: ["FUEL DELIVERY"],
        required: 5,
        period: "365 days",
      },
      {
        awardId: "traffic-tamer",
        title: "Traffic Tamer",
        icon: "🚦",
        services: ["TRAFFIC CONTROL"],
        required: 25,
        period: "365 days",
      },
      {
        awardId: "road-scholar",
        title: "Road Scholar",
        icon: "📚",
        services: ["DIAGNOSIS"],
        required: 15,
        period: "365 days",
      },
      {
        awardId: "century-club",
        title: "Century Club",
        icon: "💯",
        services: [],
        totalIncidents: true,
        required: 100,
        period: "365 days",
      },
      {
        awardId: "air-ace",
        title: "Air Ace",
        icon: "🎈",
        services: ["AIR TIRE"],
        required: 15,
        period: "365 days",
      },
      {
        awardId: "good-neighbor",
        title: "Good Neighbor",
        icon: "🚐",
        services: ["COURTESY RIDE"],
        required: 5,
        period: "365 days",
      },
      {
        awardId: "fire-fighter",
        title: "Fire Fighter",
        icon: "🧯",
        services: ["FIRE EXTINGUISHER"],
        required: 1,
        period: "365 days",
      },
      {
        awardId: "master-mechanic",
        title: "Master Mechanic",
        icon: "🔩",
        services: ["MAJOR REPAIR"],
        required: 10,
        period: "365 days",
      },
      {
        awardId: "debris-destroyer",
        title: "Debris Destroyer",
        icon: "🧹",
        services: ["DEBRIS REMOVAL"],
        required: 20,
        period: "365 days",
      },
      {
        awardId: "bridge-builder",
        title: "Bridge Builder",
        icon: "🌉",
        services: ["TRANSLATE"],
        required: 10,
        period: "365 days",
      },
      {
        awardId: "motor-one-moving",
        title: "Motor One Moving",
        icon: "🚨",
        services: ["SAFETY ESCORT"],
        required: 10,
        period: "365 days",
      },
      {
        awardId: "hat-trick",
        title: "Hat Trick",
        icon: "🎩",
        services: [],
        hatTrick: true,
        required: 5,
        period: "365 days",
      },
      {
        awardId: "night-owl",
        title: "Night Owl",
        icon: "🦉",
        services: [],
        nightOwl: true,
        required: 10,
        period: "365 days",
      },
      {
        awardId: "weekend-warrior",
        title: "Weekend Warrior",
        icon: "🗓️",
        services: [],
        weekendWarrior: true,
        required: 50,
        period: "365 days",
      },
      {
        awardId: "highway-guardian",
        title: "Highway Guardian",
        icon: "🛣️",
        services: [],
        totalIncidents: true,
        required: 250,
        period: "365 days",
      },
      {
        awardId: "swiss-army-ranger",
        title: "Swiss Army Ranger",
        icon: "🇨🇭",
        services: [],
        swissArmy: true,
        required: 6,
        period: "365 days",
      },
      {
        awardId: "holiday-hero",
        title: "Holiday Hero",
        icon: "🎄",
        services: [],
        holidayHero: true,
        required: 5,
        period: "365 days",
      },
      {
        awardId: "pacemaker",
        title: "Pacemaker",
        icon: "💓",
        services: [],
        pacemaker: true,
        required: 30,
        period: "365 days",
      },
      {
        awardId: "500-career-incidents",
        title: "500 Career Incidents",
        icon: "🎯",
        services: [],
        careerIncidents: true,
        required: 500,
        period: "career",
      },
      {
        awardId: "1000-career-incidents",
        title: "1,000 Career Incidents",
        icon: "🏛️",
        services: [],
        careerIncidents: true,
        required: 1000,
        period: "career",
      },
    ];

    const handleSearchEligibility = async () => {
      if (!awardSearchRangerId.trim()) {
        setAwardEligibilityError("Please enter a Ranger ID");
        return;
      }

      setAwardEligibilityLoading(true);
      setAwardEligibilityError(null);
      setAwardEligibilityData(null);

      try {
        // Calculate date 365 days ago
        const oneYearAgo = new Date();
        oneYearAgo.setDate(oneYearAgo.getDate() - 365);

        // Fetch incidents for this ranger
        const res = await api.get("/incidents", {
          params: {
            rangerId: awardSearchRangerId.trim().toUpperCase(),
            startDate: oneYearAgo.toISOString(),
            pageSize: 1000,
          },
        });

        const incidents = res.data?.items || res.data || [];

        // Also fetch ALL career incidents (no date filter) for career milestones
        let careerTotal = incidents.length;
        try {
          const careerRes = await api.get("/incidents", {
            params: {
              rangerId: awardSearchRangerId.trim().toUpperCase(),
              pageSize: 1,
            },
          });
          // If paginated, use total count; otherwise use array length
          careerTotal = careerRes.data?.totalCount ?? careerRes.data?.total ?? (careerRes.data?.items || careerRes.data || []).length;
        } catch (e) {
          console.warn("Could not fetch career totals, using 365-day count", e);
        }

        // Count services provided and incident types
        const serviceCounts = {};
        let totalIncidents = 0;
        const incidentTypes = {};
        const incidentsByDate = {};
        let nightOwlCount = 0;
        let weekendWarriorCount = 0;
        let holidayHeroCount = 0;
        const serviceTypesByMonth = {}; // key: "YYYY-MM", value: Set of service types

        // Major US holidays (month is 0-indexed)
        const getHolidays = (year) => {
          const holidays = [];
          // New Year's Day - Jan 1
          holidays.push(new Date(year, 0, 1).toDateString());
          // MLK Day - 3rd Monday of January
          const jan1 = new Date(year, 0, 1);
          const mlkDay = new Date(year, 0, 1 + ((1 - jan1.getDay() + 7) % 7) + 14);
          holidays.push(mlkDay.toDateString());
          // Presidents Day - 3rd Monday of February
          const feb1 = new Date(year, 1, 1);
          const presDay = new Date(year, 1, 1 + ((1 - feb1.getDay() + 7) % 7) + 14);
          holidays.push(presDay.toDateString());
          // Memorial Day - Last Monday of May
          const may31 = new Date(year, 4, 31);
          const memDay = new Date(year, 4, 31 - ((may31.getDay() + 6) % 7));
          holidays.push(memDay.toDateString());
          // Juneteenth - Jun 19
          holidays.push(new Date(year, 5, 19).toDateString());
          // Independence Day - Jul 4
          holidays.push(new Date(year, 6, 4).toDateString());
          // Labor Day - 1st Monday of September
          const sep1 = new Date(year, 8, 1);
          const laborDay = new Date(year, 8, 1 + ((1 - sep1.getDay() + 7) % 7));
          holidays.push(laborDay.toDateString());
          // Veterans Day - Nov 11
          holidays.push(new Date(year, 10, 11).toDateString());
          // Thanksgiving - 4th Thursday of November
          const nov1 = new Date(year, 10, 1);
          const thanksgiving = new Date(year, 10, 1 + ((4 - nov1.getDay() + 7) % 7) + 21);
          holidays.push(thanksgiving.toDateString());
          // Christmas Day - Dec 25
          holidays.push(new Date(year, 11, 25).toDateString());
          // Christmas Eve - Dec 24
          holidays.push(new Date(year, 11, 24).toDateString());
          // New Year's Eve - Dec 31
          holidays.push(new Date(year, 11, 31).toDateString());
          return holidays;
        };

        const currentYear = new Date().getFullYear();
        const allHolidays = new Set([
          ...getHolidays(currentYear),
          ...getHolidays(currentYear - 1),
        ]);

        incidents.forEach((incident) => {
          totalIncidents++;
          
          // Count incident types
          if (incident.incidentType) {
            const type = incident.incidentType.toUpperCase();
            incidentTypes[type] = (incidentTypes[type] || 0) + 1;
          }

          // Count services/dispositions
          if (Array.isArray(incident.servicesProvided)) {
            incident.servicesProvided.forEach((service) => {
              if (service) {
                const svc = service.toUpperCase().trim();
                serviceCounts[svc] = (serviceCounts[svc] || 0) + 1;
              }
            });
          }

          // Track incidents by date for hat trick calculation
          if (incident.occurredAtUtc) {
            const dt = new Date(incident.occurredAtUtc);
            const dateKey = dt.toLocaleDateString();
            incidentsByDate[dateKey] = (incidentsByDate[dateKey] || 0) + 1;

            // Night Owl: incidents between 10 PM and 6 AM local time
            const hours = dt.getHours();
            if (hours >= 22 || hours < 6) {
              nightOwlCount++;
            }

            // Weekend Warrior: incidents on Saturday (6) or Sunday (0)
            const day = dt.getDay();
            if (day === 0 || day === 6) {
              weekendWarriorCount++;
            }

            // Holiday Hero: incidents on major holidays
            if (allHolidays.has(dt.toDateString())) {
              holidayHeroCount++;
            }

            // Swiss Army Ranger: track distinct service types per month
            const monthKey = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
            if (!serviceTypesByMonth[monthKey]) {
              serviceTypesByMonth[monthKey] = new Set();
            }
            if (Array.isArray(incident.servicesProvided)) {
              incident.servicesProvided.forEach((svc) => {
                if (svc) serviceTypesByMonth[monthKey].add(svc.toUpperCase().trim());
              });
            }
          }
        });

        // Count hat trick days (days with 3+ incidents)
        const hatTrickDays = Object.values(incidentsByDate).filter(count => count >= 3).length;

        // Swiss Army Ranger: count months with 5+ distinct service types
        const swissArmyMonths = Object.values(serviceTypesByMonth).filter(s => s.size >= 5).length;

        // Pacemaker: find longest consecutive-day streak with at least 1 incident
        const activeDates = Object.keys(incidentsByDate)
          .map((d) => new Date(d))
          .sort((a, b) => a - b);
        let longestStreak = 0;
        let currentStreak = 0;
        for (let i = 0; i < activeDates.length; i++) {
          if (i === 0) {
            currentStreak = 1;
          } else {
            const diffDays = Math.round(
              (activeDates[i] - activeDates[i - 1]) / (1000 * 60 * 60 * 24)
            );
            currentStreak = diffDays === 1 ? currentStreak + 1 : 1;
          }
          longestStreak = Math.max(longestStreak, currentStreak);
        }

        // Calculate award progress
        const awardProgress = awardCriteria.map((criteria) => {
          let count = 0;
          
          if (criteria.hatTrick) {
            count = hatTrickDays;
          } else if (criteria.nightOwl) {
            count = nightOwlCount;
          } else if (criteria.weekendWarrior) {
            count = weekendWarriorCount;
          } else if (criteria.holidayHero) {
            count = holidayHeroCount;
          } else if (criteria.swissArmy) {
            count = swissArmyMonths;
          } else if (criteria.pacemaker) {
            count = longestStreak;
          } else if (criteria.careerIncidents) {
            count = typeof careerTotal === "number" ? careerTotal : totalIncidents;
          } else if (criteria.totalIncidents) {
            count = totalIncidents;
          } else if (criteria.incidentBased) {
            criteria.services.forEach((type) => {
              count += incidentTypes[type.toUpperCase()] || 0;
            });
          } else {
            criteria.services.forEach((svc) => {
              count += serviceCounts[svc.toUpperCase()] || 0;
            });
          }

          return {
            ...criteria,
            current: count,
            progress: Math.min(100, (count / criteria.required) * 100),
            eligible: count >= criteria.required,
          };
        });

        setAwardEligibilityData({
          rangerId: awardSearchRangerId.trim().toUpperCase(),
          totalIncidents,
          careerTotal,
          incidentTypes,
          serviceCounts,
          hatTrickDays,
          nightOwlCount,
          weekendWarriorCount,
          holidayHeroCount,
          swissArmyMonths,
          longestStreak,
          awardProgress,
          dateRange: {
            start: oneYearAgo.toLocaleDateString(),
            end: new Date().toLocaleDateString(),
          },
        });
      } catch (err) {
        console.error("Failed to fetch award eligibility:", err);
        setAwardEligibilityError(
          err.response?.data?.message || "Failed to fetch data. Please try again."
        );
      } finally {
        setAwardEligibilityLoading(false);
      }
    };

    return (
      <Box sx={{ p: 3 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 1,
            mb: 1,
            textAlign: "center",
          }}
        >
          Texas Highway Rangers Awards
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center", mb: 4 }}
        >
          Recognition for excellence, dedication, and service on the Texas roads
        </Typography>

        {/* Award Eligibility Checker */}
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            mb: 4,
            borderColor: "primary.main",
            borderWidth: 2,
          }}
        >
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <StarIcon color="primary" />
              <Typography variant="h6" fontWeight={700}>
                Award Eligibility Report
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Search a Ranger ID to check their progress toward achievement awards based on
              incident dispositions in the last 365 days.
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
              <TextField
                label="Ranger ID"
                placeholder="e.g., 101"
                value={awardSearchRangerId}
                onChange={(e) => setAwardSearchRangerId(e.target.value)}
                size="small"
                sx={{ minWidth: 200 }}
                onKeyPress={(e) => e.key === "Enter" && handleSearchEligibility()}
              />
              <Button
                variant="contained"
                onClick={handleSearchEligibility}
                disabled={awardEligibilityLoading}
                startIcon={awardEligibilityLoading ? <CircularProgress size={16} /> : null}
              >
                {awardEligibilityLoading ? "Searching..." : "Check Eligibility"}
              </Button>
            </Stack>

            {awardEligibilityError && (
              <Alert severity="error">{awardEligibilityError}</Alert>
            )}

            {awardEligibilityData && (
              <Box sx={{ mt: 2 }}>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={3}>
                  {/* Summary */}
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                      📊 Summary for Ranger {awardEligibilityData.rangerId}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Date Range: {awardEligibilityData.dateRange.start} - {awardEligibilityData.dateRange.end}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Incidents: {awardEligibilityData.totalIncidents}
                    </Typography>
                  </Box>

                  {/* Disposition Counts */}
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                      📋 All Dispositions Used
                    </Typography>
                    {Object.keys(awardEligibilityData.serviceCounts).length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No dispositions found for this ranger.
                      </Typography>
                    ) : (
                      <Stack direction="row" flexWrap="wrap" gap={1}>
                        {Object.entries(awardEligibilityData.serviceCounts)
                          .sort(([, a], [, b]) => b - a)
                          .map(([service, count]) => (
                            <Chip
                              key={service}
                              label={`${service}: ${count}`}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                      </Stack>
                    )}
                  </Box>

                  {/* Award Progress */}
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                      🏆 Achievement Award Progress
                    </Typography>
                    <Grid container spacing={2}>
                      {awardEligibilityData.awardProgress.map((award) => (
                        <Grid item xs={12} sm={6} md={4} key={award.awardId}>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 2,
                              height: "100%",
                              borderColor: award.eligible ? "success.main" : "divider",
                              borderWidth: award.eligible ? 2 : 1,
                              bgcolor: award.eligible ? "success.main" : "transparent",
                              color: award.eligible ? "success.contrastText" : "text.primary",
                            }}
                          >
                            <Stack spacing={1}>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography variant="h5">{award.icon}</Typography>
                                <Typography variant="subtitle2" fontWeight={700}>
                                  {award.title}
                                </Typography>
                                {award.eligible && (
                                  <Chip
                                    label="ELIGIBLE"
                                    size="small"
                                    color="success"
                                    sx={{ fontWeight: 700, ml: "auto" }}
                                  />
                                )}
                              </Stack>
                              <Typography variant="body2" sx={{ opacity: award.eligible ? 1 : 0.7 }}>
                                {award.current} / {award.required} ({award.period})
                              </Typography>
                              <LinearProgress
                                variant="determinate"
                                value={award.progress}
                                color={award.eligible ? "inherit" : "primary"}
                                sx={{
                                  height: 8,
                                  borderRadius: 4,
                                  bgcolor: award.eligible ? "rgba(255,255,255,0.3)" : "action.hover",
                                }}
                              />
                              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                {award.nightOwl
                                  ? "Looks for: incidents between 10 PM – 6 AM"
                                  : award.weekendWarrior
                                  ? "Looks for: incidents on Saturday or Sunday"
                                  : award.holidayHero
                                  ? "Looks for: incidents on major US holidays"
                                  : award.swissArmy
                                  ? "Looks for: months with 5+ distinct service types"
                                  : award.pacemaker
                                  ? "Looks for: longest consecutive-day response streak"
                                  : award.careerIncidents
                                  ? "Looks for: total career incidents (all time)"
                                  : award.hatTrick
                                  ? "Looks for: days with 3+ incidents"
                                  : award.totalIncidents
                                  ? "Looks for: total incidents in period"
                                  : `Looks for: ${award.services.join(", ")}`}
                              </Typography>
                            </Stack>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Stack>
              </Box>
            )}
          </Stack>
        </Paper>

        <Stack spacing={4}>
          {categories.map((category) => {
            const categoryAwards = AWARDS.filter((a) => a.category === category.id);
            if (categoryAwards.length === 0) return null;

            return (
              <Box key={category.id}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <Chip
                    label={category.label}
                    color={category.color}
                    size="small"
                    sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}
                  />
                  <Divider sx={{ flex: 1 }} />
                </Stack>

                <Grid container spacing={2}>
                  {categoryAwards.map((award) => (
                    <Grid item xs={12} sm={6} md={4} key={award.id}>
                      <Card
                        variant="outlined"
                        sx={{
                          height: "100%",
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            transform: "translateY(-4px)",
                            boxShadow: 4,
                            borderColor: `${category.color}.main`,
                          },
                        }}
                      >
                        <CardContent>
                          <Stack spacing={1.5}>
                            <Stack direction="row" alignItems="center" spacing={1.5}>
                              <Typography variant="h3" sx={{ lineHeight: 1 }}>
                                {award.icon}
                              </Typography>
                              <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 700, lineHeight: 1.2 }}
                              >
                                {award.title}
                              </Typography>
                            </Stack>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ lineHeight: 1.6 }}
                            >
                              {award.description}
                            </Typography>
                            {award.eligibility && (
                              <Box
                                sx={{
                                  mt: 1,
                                  pt: 1,
                                  borderTop: 1,
                                  borderColor: "divider",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="primary"
                                  sx={{ fontWeight: 600, display: "block", mb: 0.5 }}
                                >
                                  Eligibility
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {award.eligibility}
                                </Typography>
                              </Box>
                            )}
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            );
          })}
        </Stack>
      </Box>
    );
  };

  // Get rangers for a specific region/division
  // Note: /profiles/all already filters out disabled users, so no need to check isDisabled
  const getRangersForRegion = (division) => {
    return allUsers.filter(
      (user) =>
        user.division &&
        user.division.toLowerCase() === division.toLowerCase()
    );
  };

  // Get lead ranger for a region (supervisor or first ranger with "lead" in role)
  const getLeadRanger = (division) => {
    const regionUsers = getRangersForRegion(division);
    // First try to find a supervisor
    const supervisor = regionUsers.find(
      (user) => user.role && user.role.toLowerCase() === "supervisor"
    );
    if (supervisor) return supervisor;
    // Otherwise return the first user (could be enhanced with a "lead" field)
    return regionUsers[0] || null;
  };

  const handleRegionClick = (region) => {
    setSelectedRegion(region);
    setRegionDialogOpen(true);
  };

  const handleCloseRegionDialog = () => {
    setRegionDialogOpen(false);
    setSelectedRegion(null);
  };

  const renderRegionMap = () => {
    if (usersLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      );
    }

    // Get color for a county based on its FIPS code
    const getCountyColor = (fips) => {
      const countyInfo = COUNTY_FIPS[fips];
      if (countyInfo) {
        const region = REGIONS.find((r) => r.id === countyInfo.region);
        return region ? region.color : "#E5E7EB";
      }
      return "#E5E7EB"; // Default gray for non-highlighted counties
    };

    // Get region for a county
    const getCountyRegion = (fips) => {
      const countyInfo = COUNTY_FIPS[fips];
      if (countyInfo) {
        return REGIONS.find((r) => r.id === countyInfo.region);
      }
      return null;
    };

    return (
      <Box sx={{ p: 2 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 1,
            mb: 1,
            textAlign: "center",
          }}
        >
          Texas Highway Rangers Regions
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center", mb: 3 }}
        >
          Click on a highlighted region to view assigned rangers
        </Typography>

        {/* Legend */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 3,
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          {REGIONS.map((region) => (
            <Box
              key={region.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                cursor: "pointer",
                p: 1,
                borderRadius: 1,
                "&:hover": { backgroundColor: "action.hover" },
              }}
              onClick={() => handleRegionClick(region)}
            >
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  backgroundColor: region.color,
                  borderRadius: 1,
                  border: "2px solid",
                  borderColor: region.color,
                }}
              />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {region.name} ({getRangersForRegion(region.division).length} Rangers)
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Map Container */}
        <Paper
          elevation={3}
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            backgroundColor: "#f8fafc",
          }}
        >
          <ComposableMap
            projection="geoAlbersUsa"
            projectionConfig={{
              scale: 1200,
            }}
            style={{
              width: "100%",
              height: "auto",
            }}
          >
            <ZoomableGroup
              center={[-96, 33]}
              zoom={1}
              minZoom={0.5}
              maxZoom={6}
            >
              <Geographies geography={COUNTIES_GEO_URL}>
                {({ geographies }) =>
                  geographies
                    .filter((geo) => geo.properties.STATE === "48" || geo.properties.STATE === "40" || geo.properties.STATE === "45") // Texas + Oklahoma + South Carolina
                    .map((geo) => {
                      const fips = geo.id;
                      const countyInfo = COUNTY_FIPS[fips];
                      const isHighlighted = !!countyInfo;
                      const region = getCountyRegion(fips);

                      return (
                        <Tooltip
                          key={geo.rsmKey}
                          title={
                            countyInfo
                              ? `${countyInfo.name} - ${region?.name || "Unknown"}`
                              : ""
                          }
                          arrow
                        >
                          <Geography
                            geography={geo}
                            onClick={() => {
                              if (region) {
                                handleRegionClick(region);
                              }
                            }}
                            style={{
                              default: {
                                fill: getCountyColor(fips),
                                stroke: isHighlighted ? "#fff" : "#D1D5DB",
                                strokeWidth: isHighlighted ? 1.5 : 0.5,
                                outline: "none",
                                cursor: isHighlighted ? "pointer" : "default",
                              },
                              hover: {
                                fill: isHighlighted
                                  ? `${getCountyColor(fips)}CC`
                                  : "#E5E7EB",
                                stroke: isHighlighted ? "#fff" : "#D1D5DB",
                                strokeWidth: isHighlighted ? 2 : 0.5,
                                outline: "none",
                                cursor: isHighlighted ? "pointer" : "default",
                              },
                              pressed: {
                                fill: isHighlighted
                                  ? `${getCountyColor(fips)}99`
                                  : "#E5E7EB",
                                stroke: "#fff",
                                strokeWidth: isHighlighted ? 2 : 0.5,
                                outline: "none",
                              },
                            }}
                          />
                        </Tooltip>
                      );
                    })
                }
              </Geographies>
              {/* Region Labels */}
              {REGIONS.map((region) => (
                <Annotation
                  key={region.id}
                  subject={region.center}
                  dx={0}
                  dy={0}
                  connectorProps={{}}
                >
                  <text
                    textAnchor="middle"
                    alignmentBaseline="middle"
                    style={{
                      fontFamily: "system-ui",
                      fontSize: 10,
                      fontWeight: "bold",
                      fill: "#fff",
                      textShadow: "1px 1px 2px rgba(0,0,0,0.5)",
                      cursor: "pointer",
                    }}
                    onClick={() => handleRegionClick(region)}
                  >
                    {region.name.replace(" Region", "")}
                  </text>
                </Annotation>
              ))}
            </ZoomableGroup>
          </ComposableMap>
        </Paper>

        {/* Region Cards below the map */}
        <Grid container spacing={3} justifyContent="center" sx={{ mt: 3 }}>
          {REGIONS.map((region) => {
            const rangers = getRangersForRegion(region.division);
            const leadRanger = getLeadRanger(region.division);

            return (
              <Grid item xs={12} sm={6} md={5} key={region.id}>
                <Card
                  sx={{
                    height: "100%",
                    border: 3,
                    borderColor: region.color,
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 6,
                    },
                  }}
                >
                  <CardActionArea
                    onClick={() => handleRegionClick(region)}
                    sx={{ height: "100%" }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 2,
                        }}
                      >
                        <PlaceIcon
                          sx={{ fontSize: 40, color: region.color, mr: 2 }}
                        />
                        <Typography
                          variant="h5"
                          sx={{ fontWeight: 700, color: region.color }}
                        >
                          {region.name}
                        </Typography>
                      </Box>

                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ mb: 1, textTransform: "uppercase", letterSpacing: 1 }}
                      >
                        Coverage Area
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        {region.counties.map((county) => (
                          <Chip
                            key={county}
                            label={county}
                            size="small"
                            sx={{
                              mr: 0.5,
                              mb: 0.5,
                              backgroundColor: `${region.color}20`,
                              borderColor: region.color,
                              border: 1,
                            }}
                          />
                        ))}
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Lead Ranger
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {leadRanger
                              ? leadRanger.name || leadRanger.rangerId || leadRanger.username
                              : "Not Assigned"}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: "right" }}>
                          <Typography variant="body2" color="text.secondary">
                            Total Rangers
                          </Typography>
                          <Typography
                            variant="h5"
                            sx={{ fontWeight: 700, color: region.color }}
                          >
                            {rangers.length}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Region Detail Dialog */}
        <Dialog
          open={regionDialogOpen}
          onClose={handleCloseRegionDialog}
          maxWidth="sm"
          fullWidth
        >
          {selectedRegion && (
            <>
              <DialogTitle
                sx={{
                  backgroundColor: selectedRegion.color,
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <PlaceIcon sx={{ mr: 1 }} />
                  {selectedRegion.name}
                </Box>
                <IconButton
                  onClick={handleCloseRegionDialog}
                  sx={{ color: "#fff" }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent sx={{ mt: 2 }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ textTransform: "uppercase", letterSpacing: 1, mb: 1 }}
                >
                  Coverage Area
                </Typography>
                <Box sx={{ mb: 3 }}>
                  {selectedRegion.counties.map((county) => (
                    <Chip
                      key={county}
                      label={county}
                      sx={{
                        mr: 0.5,
                        mb: 0.5,
                        backgroundColor: `${selectedRegion.color}20`,
                        borderColor: selectedRegion.color,
                        border: 1,
                      }}
                    />
                  ))}
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Lead Ranger Section */}
                {(() => {
                  const leadRanger = getLeadRanger(selectedRegion.division);
                  return (
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ textTransform: "uppercase", letterSpacing: 1, mb: 1 }}
                      >
                        Lead Ranger
                      </Typography>
                      {leadRanger ? (
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            backgroundColor: `${selectedRegion.color}10`,
                            borderColor: selectedRegion.color,
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <StarIcon
                              sx={{ color: selectedRegion.color, mr: 1 }}
                            />
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {leadRanger.name || "Name Not Set"}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {leadRanger.rangerId || leadRanger.username} •{" "}
                                {leadRanger.role || "Ranger"}
                              </Typography>
                              {leadRanger.email && (
                                <Typography variant="body2" color="text.secondary">
                                  {leadRanger.email}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Paper>
                      ) : (
                        <Typography color="text.secondary">
                          No lead ranger assigned
                        </Typography>
                      )}
                    </Box>
                  );
                })()}

                {/* Rangers List */}
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ textTransform: "uppercase", letterSpacing: 1, mb: 1 }}
                >
                  Assigned Rangers ({getRangersForRegion(selectedRegion.division).length})
                </Typography>
                {getRangersForRegion(selectedRegion.division).length > 0 ? (
                  <List dense>
                    {getRangersForRegion(selectedRegion.division).map((ranger) => (
                      <ListItem key={ranger.id}>
                        <ListItemIcon>
                          <PersonIcon sx={{ color: selectedRegion.color }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={ranger.name || ranger.username}
                          secondary={
                            <>
                              {ranger.rangerId && `${ranger.rangerId} • `}
                              {ranger.role || "Ranger"}
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography color="text.secondary" sx={{ py: 2 }}>
                    No rangers currently assigned to this region
                  </Typography>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseRegionDialog}>Close</Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Box>
    );
  };

  const renderPatrolCalendar = () => {
    return (
      <Box>
        {/* Header */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
              <EventIcon /> Patrol Calendar
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sign up for upcoming patrol shifts and events
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant={showPastEvents ? "contained" : "outlined"}
              size="small"
              onClick={() => setShowPastEvents(!showPastEvents)}
            >
              {showPastEvents ? "Hide Past" : "Show Past"}
            </Button>
            {isSupervisorOrAdmin && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenCreateEvent}
              >
                Create Event
              </Button>
            )}
          </Stack>
        </Stack>

        {patrolCalendarAlert && (
          <Alert 
            severity={patrolCalendarAlert.severity} 
            onClose={() => setPatrolCalendarAlert(null)}
            sx={{ mb: 2 }}
          >
            {patrolCalendarAlert.message}
          </Alert>
        )}

        {patrolEventsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : patrolEvents.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <EventIcon sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No upcoming events
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isSupervisorOrAdmin 
                ? "Create an event to get started" 
                : "Check back later for upcoming patrol opportunities"}
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {patrolEvents.map((event) => {
              const isSignedUp = isUserSignedUp(event);
              const isPast = new Date(event.endTimeUtc) < new Date();
              
              return (
                <Grid item xs={12} md={6} key={event.id}>
                  <Card 
                    sx={{ 
                      opacity: event.isCancelled ? 0.6 : 1,
                      borderLeft: 4,
                      borderColor: event.isCancelled 
                        ? "error.main" 
                        : isSignedUp 
                          ? "success.main" 
                          : isPast 
                            ? "grey.400"
                            : "primary.main",
                    }}
                  >
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Box sx={{ flex: 1 }}>
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                              {event.title}
                            </Typography>
                            {event.eventType && event.eventType !== "Other" && (
                              <Chip 
                                label={event.eventType} 
                                size="small" 
                                color="info"
                                variant="outlined"
                              />
                            )}
                            {event.isCancelled && (
                              <Chip label="CANCELLED" size="small" color="error" />
                            )}
                            {isSignedUp && !event.isCancelled && (
                              <Chip 
                                icon={<CheckCircleIcon />} 
                                label="Signed Up" 
                                size="small" 
                                color="success" 
                              />
                            )}
                          </Stack>
                          
                          {event.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {event.description}
                            </Typography>
                          )}
                          
                          <Stack spacing={0.5} sx={{ mb: 2 }}>
                            <Typography variant="body2">
                              <strong>When:</strong> {formatEventDateTime(event.startTimeUtc)} – {formatEventDateTime(event.endTimeUtc)} CST
                            </Typography>
                            {(event.locationTitle || event.location) && (
                              <Typography variant="body2">
                                <strong>Where:</strong> {event.locationTitle ? `${event.locationTitle}${event.location ? ` - ${event.location}` : ""}` : event.location}
                              </Typography>
                            )}
                            {event.region && (
                              <Typography variant="body2">
                                <strong>Region:</strong> {event.region}
                              </Typography>
                            )}
                            {event.radioChannel && (
                              <Typography variant="body2">
                                <strong>Radio Channel:</strong> {event.radioChannel}
                              </Typography>
                            )}
                          </Stack>

                          {/* Mini Map if coordinates exist */}
                          {event.latitude && event.longitude && (
                            <Box sx={{ height: 120, width: "100%", borderRadius: 1, overflow: "hidden", mb: 2, border: 1, borderColor: "divider" }}>
                              <iframe
                                title={`Map for ${event.title}`}
                                width="100%"
                                height="100%"
                                frameBorder="0"
                                style={{ border: 0 }}
                                src={`https://www.openstreetmap.org/export/embed.html?bbox=${event.longitude - 0.005}%2C${event.latitude - 0.005}%2C${event.longitude + 0.005}%2C${event.latitude + 0.005}&layer=mapnik&marker=${event.latitude}%2C${event.longitude}`}
                              />
                            </Box>
                          )}

                          {/* Signups */}
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                              Signed Up ({event.signups?.length || 0})
                            </Typography>
                            {event.signups?.length > 0 ? (
                              <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                {event.signups.map((signup) => (
                                  <Chip
                                    key={signup.id}
                                    size="small"
                                    icon={<PersonIcon />}
                                    label={signup.name || signup.username || signup.rangerId}
                                    onDelete={
                                      isSupervisorOrAdmin && !event.isCancelled && !isPast
                                        ? () => handleRemoveUserFromEvent(event.id, signup.userId)
                                        : undefined
                                    }
                                    title={
                                      signup.signedUpByUsername 
                                        ? `Added by ${signup.signedUpByUsername}${signup.notes ? ` - ${signup.notes}` : ""}`
                                        : signup.notes || undefined
                                    }
                                  />
                                ))}
                              </Stack>
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                                No signups yet
                              </Typography>
                            )}
                          </Box>

                          {/* Actions */}
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {!event.isCancelled && !isPast && (
                              isSignedUp ? (
                                <Button
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  onClick={() => handleCancelOwnSignup(event.id)}
                                >
                                  Cancel My Signup
                                </Button>
                              ) : (
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleSelfSignup(event.id)}
                                >
                                  Sign Up
                                </Button>
                              )
                            )}
                            
                            {isSupervisorOrAdmin && !event.isCancelled && !isPast && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<PersonAddIcon />}
                                onClick={() => handleOpenSignupDialog(event)}
                              >
                                Add User
                              </Button>
                            )}
                            
                            {isSupervisorOrAdmin && (
                              <>
                                <Tooltip title="Edit">
                                  <IconButton size="small" onClick={() => handleOpenEditEvent(event)}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={event.isCancelled ? "Restore" : "Cancel Event"}>
                                  <IconButton 
                                    size="small" 
                                    color={event.isCancelled ? "success" : "warning"}
                                    onClick={() => handleCancelEvent(event)}
                                  >
                                    {event.isCancelled ? <CheckCircleIcon fontSize="small" /> : <CancelIcon fontSize="small" />}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton 
                                    size="small" 
                                    color="error"
                                    onClick={() => handleDeleteEvent(event.id)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </Stack>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* Create/Edit Event Dialog */}
        <Dialog open={eventDialogOpen} onClose={handleCloseEventDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {eventDialogMode === "create" ? "Create Event" : "Edit Event"}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Title"
                value={eventForm.title}
                onChange={handleEventFormChange("title")}
                fullWidth
                required
              />

              {/* Event Type */}
              <FormControl fullWidth>
                <InputLabel>Event Type</InputLabel>
                <Select
                  value={eventForm.eventType}
                  onChange={handleEventFormChange("eventType")}
                  label="Event Type"
                >
                  <MenuItem value="Routine Inspection">Routine Inspection</MenuItem>
                  <MenuItem value="Public Service">Public Service</MenuItem>
                  <MenuItem value="Emergency Assist">Emergency Assist</MenuItem>
                  <MenuItem value="Meeting">Meeting</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Description"
                value={eventForm.description}
                onChange={handleEventFormChange("description")}
                fullWidth
                multiline
                rows={2}
              />

              {/* Region */}
              <FormControl fullWidth>
                <InputLabel>Region</InputLabel>
                <Select
                  value={eventForm.region}
                  onChange={handleEventFormChange("region")}
                  label="Region"
                >
                  <MenuItem value="">
                    <em>Select Region</em>
                  </MenuItem>
                  <MenuItem value="Statewide">Statewide</MenuItem>
                  {REGIONS.map((region) => (
                    <MenuItem key={region.id} value={region.division}>
                      {region.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Radio Channel */}
              <TextField
                label="Radio Channel"
                value={eventForm.radioChannel}
                onChange={handleEventFormChange("radioChannel")}
                fullWidth
                placeholder="e.g., TAC 1, OPS 1"
              />

              {/* Image Upload */}
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Event Image (optional - displayed in dashboard modal)
                </Typography>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={imageUploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                  disabled={imageUploading}
                  fullWidth
                >
                  {imageUploading ? "Uploading..." : "Upload Image"}
                  <input
                    type="file"
                    hidden
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    onChange={handleImageUpload}
                  />
                </Button>
              </Box>

              {/* Image Preview */}
              {eventForm.imageUrl && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">Image Preview:</Typography>
                  <Box sx={{ position: "relative" }}>
                    <Box
                      component="img"
                      src={eventForm.imageUrl}
                      alt="Event preview"
                      sx={{
                        width: "100%",
                        maxHeight: 200,
                        objectFit: "cover",
                        borderRadius: 1,
                        mt: 0.5,
                        border: 1,
                        borderColor: "divider",
                      }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => setEventForm((prev) => ({ ...prev, imageUrl: "" }))}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        bgcolor: "rgba(0,0,0,0.5)",
                        color: "white",
                        "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              )}

              {/* Location Section */}
              <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 600 }}>
                Location
              </Typography>
              
              <TextField
                label="Location Name/Title"
                value={eventForm.locationTitle}
                onChange={handleEventFormChange("locationTitle")}
                fullWidth
                placeholder="e.g., Regional Office, Stadium, etc."
              />

              <Stack direction="row" spacing={1}>
                <TextField
                  label="Address"
                  value={eventForm.location}
                  onChange={handleEventFormChange("location")}
                  fullWidth
                  placeholder="Enter full address"
                />
                <Button 
                  variant="outlined" 
                  onClick={handleGeocodeAddress}
                  sx={{ minWidth: 120 }}
                >
                  Find on Map
                </Button>
              </Stack>

              <Stack direction="row" spacing={2}>
                <TextField
                  label="Latitude"
                  value={eventForm.latitude}
                  onChange={handleEventFormChange("latitude")}
                  type="number"
                  inputProps={{ step: "any" }}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Longitude"
                  value={eventForm.longitude}
                  onChange={handleEventFormChange("longitude")}
                  type="number"
                  inputProps={{ step: "any" }}
                  sx={{ flex: 1 }}
                />
              </Stack>

              {/* Map Preview */}
              {eventForm.latitude && eventForm.longitude && (
                <Box sx={{ height: 200, borderRadius: 1, overflow: "hidden", border: 1, borderColor: "divider" }}>
                  <iframe
                    title="Event Location Map"
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    style={{ border: 0 }}
                    src={`https://www.openstreetmap.org/export/embed.html?bbox=${parseFloat(eventForm.longitude) - 0.01}%2C${parseFloat(eventForm.latitude) - 0.01}%2C${parseFloat(eventForm.longitude) + 0.01}%2C${parseFloat(eventForm.latitude) + 0.01}&layer=mapnik&marker=${eventForm.latitude}%2C${eventForm.longitude}`}
                    allowFullScreen
                  />
                </Box>
              )}

              {/* Date/Time Section */}
              <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 600 }}>
                Date & Time (CST)
              </Typography>

              <TextField
                label="Start Time (CST)"
                type="datetime-local"
                value={eventForm.startTimeUtc}
                onChange={handleEventFormChange("startTimeUtc")}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                helperText="Enter time in Central Standard Time"
              />
              <TextField
                label="End Time (CST)"
                type="datetime-local"
                value={eventForm.endTimeUtc}
                onChange={handleEventFormChange("endTimeUtc")}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                helperText="Enter time in Central Standard Time"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEventDialog}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveEvent}>
              {eventDialogMode === "create" ? "Create" : "Save"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Supervisor Signup Dialog */}
        <Dialog open={signupDialogOpen} onClose={handleCloseSignupDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Add User to Event</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Select a user to sign up for "{signupTargetEvent?.title}"
              </Typography>
              <Autocomplete
                options={availableUsersForSignup}
                getOptionLabel={(option) => 
                  `${option.name || option.username}${option.rangerId ? ` (${option.rangerId})` : ""}`
                }
                value={selectedUserForSignup}
                onChange={(_, newValue) => setSelectedUserForSignup(newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Select User" placeholder="Search users..." />
                )}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
              />
              <TextField
                label="Notes (optional)"
                value={signupNotes}
                onChange={(e) => setSignupNotes(e.target.value)}
                fullWidth
                placeholder="Any notes about this signup..."
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseSignupDialog}>Cancel</Button>
            <Button 
              variant="contained" 
              onClick={handleSupervisorSignup}
              disabled={!selectedUserForSignup}
            >
              Sign Up User
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  // ─── Repeater Map ───────────────────────────────────────────────
  const resetRepeaterForm = () => {
    setRepeaterForm({
      name: "", latitude: "", longitude: "", frequency: "", inputFrequency: "",
      txTone: "", rxTone: "", mode: "FM", bandwidth: "25 kHz",
      coverageRadiusMiles: "6.2", heightFeet: "", notes: "", callsign: "",
    });
  };

  const openCreateRepeater = () => {
    resetRepeaterForm();
    setRepeaterDialogMode("create");
    setEditRepeater(null);
    setRepeaterDialogOpen(true);
  };

  const openEditRepeaterDialog = (rep) => {
    setRepeaterForm({
      name: rep.name || "",
      latitude: String(rep.latitude ?? ""),
      longitude: String(rep.longitude ?? ""),
      frequency: rep.frequency || "",
      inputFrequency: rep.inputFrequency || "",
      txTone: rep.txTone || "",
      rxTone: rep.rxTone || "",
      mode: rep.mode || "FM",
      bandwidth: rep.bandwidth || "25 kHz",
      coverageRadiusMiles: String(Number((kmToMiles(rep.coverageRadiusKm ?? 10) ?? 6.2).toFixed(1))),
      heightFeet: rep.heightMeters == null ? "" : String(Math.round(metersToFeet(rep.heightMeters))),
      notes: rep.notes || "",
      callsign: rep.callsign || "",
    });
    setRepeaterDialogMode("edit");
    setEditRepeater(rep);
    setRepeaterDialogOpen(true);
  };

  const handleRepeaterSave = async () => {
    setRepeaterSaving(true);
    setRepeaterAlert(null);
    try {
      const payload = {
        name: repeaterForm.name,
        latitude: parseFloat(repeaterForm.latitude),
        longitude: parseFloat(repeaterForm.longitude),
        frequency: repeaterForm.frequency || null,
        inputFrequency: repeaterForm.inputFrequency || null,
        txTone: repeaterForm.txTone || null,
        rxTone: repeaterForm.rxTone || null,
        mode: repeaterForm.mode || "FM",
        bandwidth: repeaterForm.bandwidth || null,
        coverageRadiusKm: milesToKm(repeaterForm.coverageRadiusMiles) || 10,
        heightMeters: repeaterForm.heightFeet ? feetToMeters(repeaterForm.heightFeet) : null,
        notes: repeaterForm.notes || null,
        callsign: repeaterForm.callsign || null,
      };
      if (repeaterDialogMode === "edit" && editRepeater) {
        await api.put(`/radio-repeaters/${editRepeater.id}`, payload);
        setRepeaterAlert({ severity: "success", message: "Repeater updated." });
      } else {
        await api.post("/radio-repeaters", payload);
        setRepeaterAlert({ severity: "success", message: "Repeater created." });
      }
      setRepeaterDialogOpen(false);
      fetchRepeaters();
    } catch (err) {
      console.error(err);
      setRepeaterAlert({ severity: "error", message: err.response?.data || "Failed to save repeater." });
    } finally {
      setRepeaterSaving(false);
    }
  };

  const handleDeleteRepeater = async (id) => {
    if (!window.confirm("Delete this repeater?")) return;
    try {
      await api.delete(`/radio-repeaters/${id}`);
      setRepeaterAlert({ severity: "success", message: "Repeater deleted." });
      fetchRepeaters();
    } catch (err) {
      console.error(err);
      setRepeaterAlert({ severity: "error", message: "Failed to delete repeater." });
    }
  };

  // Custom tower icon for repeater markers
  const towerIcon = useMemo(() => L.divIcon({
    html: '<div style="font-size:24px;text-align:center;">📡</div>',
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    className: "",
  }), []);

  const renderRepeaterMap = () => {
    const isAdmin = user?.role === "admin";
    const texasCenter = [31.5, -99.5];

    return (
      <Box>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6">Radio Repeater Infrastructure</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControlLabel
              control={<Checkbox checked={showCoverage} onChange={(e) => setShowCoverage(e.target.checked)} size="small" />}
              label={<Typography variant="body2">Coverage Circles</Typography>}
            />
            {isAdmin && (
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCreateRepeater}>
                Add Repeater
              </Button>
            )}
          </Stack>
        </Stack>

        {repeaterAlert && (
          <Alert severity={repeaterAlert.severity} sx={{ mb: 2 }} onClose={() => setRepeaterAlert(null)}>
            {repeaterAlert.message}
          </Alert>
        )}

        {repeatersLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
        ) : (
          <>
            <Paper sx={{ height: 520, overflow: "hidden", borderRadius: 2, mb: 3 }}>
              <MapContainer center={texasCenter} zoom={6} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
                <BasemapTiles dark />
                {repeaters.map((rep) => (
                  <React.Fragment key={rep.id}>
                    {showCoverage && rep.coverageRadiusKm > 0 && (
                      <Circle
                        center={[rep.latitude, rep.longitude]}
                        radius={rep.coverageRadiusKm * 1000}
                        pathOptions={{ color: "#00e676", fillColor: "#00e676", fillOpacity: 0.08, weight: 1 }}
                      />
                    )}
                    <Marker position={[rep.latitude, rep.longitude]} icon={towerIcon}>
                      <Popup maxWidth={320}>
                        <Box sx={{ minWidth: 220 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: "text.primary" }}>
                            {rep.name}
                          </Typography>
                          {rep.callsign && <Typography variant="caption" display="block" sx={{ color: "text.secondary" }}>Callsign: {rep.callsign}</Typography>}
                          {rep.frequency && <Typography variant="caption" display="block" sx={{ fontFamily: "monospace" }}>Output Freq: {rep.frequency} MHz</Typography>}
                          {rep.inputFrequency && <Typography variant="caption" display="block" sx={{ fontFamily: "monospace" }}>Input Freq: {rep.inputFrequency} MHz</Typography>}
                          {rep.txTone && <Typography variant="caption" display="block" sx={{ fontFamily: "monospace" }}>TX Tone: {rep.txTone}</Typography>}
                          {rep.rxTone && <Typography variant="caption" display="block" sx={{ fontFamily: "monospace" }}>RX Tone: {rep.rxTone}</Typography>}
                          <Typography variant="caption" display="block">Mode: {rep.mode}{rep.bandwidth ? ` • ${rep.bandwidth}` : ""}</Typography>
                          {rep.heightMeters != null && <Typography variant="caption" display="block">Height: {Math.round(metersToFeet(rep.heightMeters))} ft</Typography>}
                          {rep.coverageRadiusKm > 0 && <Typography variant="caption" display="block">Coverage: ~{(kmToMiles(rep.coverageRadiusKm) ?? 0).toFixed(1)} mi</Typography>}
                          {rep.notes && <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: "italic" }}>{rep.notes}</Typography>}
                          {isAdmin && (
                            <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
                              <IconButton size="small" onClick={() => openEditRepeaterDialog(rep)}><EditIcon fontSize="small" /></IconButton>
                              <IconButton size="small" color="error" onClick={() => handleDeleteRepeater(rep.id)}><DeleteIcon fontSize="small" /></IconButton>
                            </Stack>
                          )}
                        </Box>
                      </Popup>
                    </Marker>
                  </React.Fragment>
                ))}
              </MapContainer>
            </Paper>

            {/* Repeater Summary Table */}
            {repeaters.length > 0 && (
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Repeater Directory ({repeaters.length})</Typography>
                <TableContainer sx={{ maxHeight: 350 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Callsign</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Output Freq</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Input Freq</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>TX Tone</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>RX Tone</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Mode</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>BW</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Height</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Coverage</TableCell>
                        {isAdmin && <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {repeaters.map((rep) => (
                        <TableRow key={rep.id} hover>
                          <TableCell>{rep.name}</TableCell>
                          <TableCell sx={{ fontFamily: "monospace" }}>{rep.callsign || "—"}</TableCell>
                          <TableCell sx={{ fontFamily: "monospace" }}>{rep.frequency || "—"}</TableCell>
                          <TableCell sx={{ fontFamily: "monospace" }}>{rep.inputFrequency || "—"}</TableCell>
                          <TableCell sx={{ fontFamily: "monospace" }}>{rep.txTone || "—"}</TableCell>
                          <TableCell sx={{ fontFamily: "monospace" }}>{rep.rxTone || "—"}</TableCell>
                          <TableCell>{rep.mode}</TableCell>
                          <TableCell>{rep.bandwidth || "—"}</TableCell>
                          <TableCell>{rep.heightMeters != null ? `${Math.round(metersToFeet(rep.heightMeters))} ft` : "—"}</TableCell>
                          <TableCell>{rep.coverageRadiusKm > 0 ? `${(kmToMiles(rep.coverageRadiusKm) ?? 0).toFixed(1)} mi` : "—"}</TableCell>
                          {isAdmin && (
                            <TableCell>
                              <IconButton size="small" onClick={() => openEditRepeaterDialog(rep)}><EditIcon fontSize="small" /></IconButton>
                              <IconButton size="small" color="error" onClick={() => handleDeleteRepeater(rep.id)}><DeleteIcon fontSize="small" /></IconButton>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {repeaters.length === 0 && !repeatersLoading && (
              <Alert severity="info">No repeaters configured yet. {isAdmin ? "Click 'Add Repeater' to get started." : "Contact an admin to add repeater data."}</Alert>
            )}
          </>
        )}

        {/* Repeater Create/Edit Dialog */}
        <Dialog open={repeaterDialogOpen} onClose={() => !repeaterSaving && setRepeaterDialogOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>{repeaterDialogMode === "edit" ? "Edit Repeater" : "Add Repeater"}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="Name" value={repeaterForm.name} onChange={(e) => setRepeaterForm(p => ({ ...p, name: e.target.value }))} required fullWidth />
              <Stack direction="row" spacing={2}>
                <TextField label="Latitude" value={repeaterForm.latitude} onChange={(e) => setRepeaterForm(p => ({ ...p, latitude: e.target.value }))} required fullWidth type="number" inputProps={{ step: "any" }} />
                <TextField label="Longitude" value={repeaterForm.longitude} onChange={(e) => setRepeaterForm(p => ({ ...p, longitude: e.target.value }))} required fullWidth type="number" inputProps={{ step: "any" }} />
              </Stack>
              <TextField label="Callsign" value={repeaterForm.callsign} onChange={(e) => setRepeaterForm(p => ({ ...p, callsign: e.target.value }))} fullWidth />
              <Stack direction="row" spacing={2}>
                <TextField label="Output Frequency (MHz)" value={repeaterForm.frequency} onChange={(e) => setRepeaterForm(p => ({ ...p, frequency: e.target.value }))} fullWidth />
                <TextField label="Input Frequency (MHz)" value={repeaterForm.inputFrequency} onChange={(e) => setRepeaterForm(p => ({ ...p, inputFrequency: e.target.value }))} fullWidth />
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField select label="Mode" value={repeaterForm.mode} onChange={(e) => setRepeaterForm(p => ({ ...p, mode: e.target.value }))} fullWidth>
                  {["FM", "DMR", "P25", "NXDN", "DStar", "Mixed"].map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                </TextField>
                <TextField select label="Bandwidth" value={repeaterForm.bandwidth} onChange={(e) => setRepeaterForm(p => ({ ...p, bandwidth: e.target.value }))} fullWidth>
                  {["25 kHz", "12.5 kHz", "6.25 kHz"].map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                </TextField>
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField label="TX Tone" value={repeaterForm.txTone} onChange={(e) => setRepeaterForm(p => ({ ...p, txTone: e.target.value }))} fullWidth />
                <TextField label="RX Tone" value={repeaterForm.rxTone} onChange={(e) => setRepeaterForm(p => ({ ...p, rxTone: e.target.value }))} fullWidth />
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField label="Coverage (miles)" value={repeaterForm.coverageRadiusMiles} onChange={(e) => setRepeaterForm(p => ({ ...p, coverageRadiusMiles: e.target.value }))} fullWidth type="number" inputProps={{ step: "0.1" }} />
                <TextField label="Height (feet AMSL)" value={repeaterForm.heightFeet} onChange={(e) => setRepeaterForm(p => ({ ...p, heightFeet: e.target.value }))} fullWidth type="number" />
              </Stack>
              <TextField label="Notes" value={repeaterForm.notes} onChange={(e) => setRepeaterForm(p => ({ ...p, notes: e.target.value }))} fullWidth multiline rows={2} />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRepeaterDialogOpen(false)} disabled={repeaterSaving}>Cancel</Button>
            <Button variant="contained" onClick={handleRepeaterSave} disabled={repeaterSaving || !repeaterForm.name || !repeaterForm.latitude || !repeaterForm.longitude}>
              {repeaterSaving ? "Saving..." : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  // ─── Regional Frequencies ───────────────────────────────────────
  const resetRegFreqForm = () => {
    setRegFreqForm({
      region: "", channelName: "", frequency: "",
      txTone: "", rxTone: "", mode: "FM", bandwidth: "25 kHz",
      assignment: "", notes: "", sortOrder: "0",
    });
  };

  const openCreateRegFreq = () => {
    resetRegFreqForm();
    setRegFreqDialogMode("create");
    setEditRegFreq(null);
    setRegFreqDialogOpen(true);
  };

  const openEditRegFreqDialog = (freq) => {
    setRegFreqForm({
      region: freq.region || "",
      channelName: freq.channelName || "",
      frequency: freq.frequency || "",
      txTone: freq.txTone || "",
      rxTone: freq.rxTone || "",
      mode: freq.mode || "FM",
      bandwidth: freq.bandwidth || "25 kHz",
      assignment: freq.assignment || "",
      notes: freq.notes || "",
      sortOrder: String(freq.sortOrder ?? "0"),
    });
    setRegFreqDialogMode("edit");
    setEditRegFreq(freq);
    setRegFreqDialogOpen(true);
  };

  const handleRegFreqSave = async () => {
    setRegFreqSaving(true);
    setRegFreqAlert(null);
    try {
      const payload = {
        region: regFreqForm.region,
        channelName: regFreqForm.channelName,
        frequency: regFreqForm.frequency,
        txTone: regFreqForm.txTone || null,
        rxTone: regFreqForm.rxTone || null,
        mode: regFreqForm.mode || "FM",
        bandwidth: regFreqForm.bandwidth || null,
        assignment: regFreqForm.assignment || null,
        notes: regFreqForm.notes || null,
        sortOrder: parseInt(regFreqForm.sortOrder) || 0,
      };
      if (regFreqDialogMode === "edit" && editRegFreq) {
        await api.put(`/regional-frequencies/${editRegFreq.id}`, payload);
        setRegFreqAlert({ severity: "success", message: "Frequency updated." });
      } else {
        await api.post("/regional-frequencies", payload);
        setRegFreqAlert({ severity: "success", message: "Frequency created." });
      }
      setRegFreqDialogOpen(false);
      fetchRegionalFrequencies();
    } catch (err) {
      console.error(err);
      setRegFreqAlert({ severity: "error", message: err.response?.data || "Failed to save frequency." });
    } finally {
      setRegFreqSaving(false);
    }
  };

  const handleDeleteRegFreq = async (id) => {
    if (!window.confirm("Delete this frequency?")) return;
    try {
      await api.delete(`/regional-frequencies/${id}`);
      setRegFreqAlert({ severity: "success", message: "Frequency deleted." });
      fetchRegionalFrequencies();
    } catch (err) {
      console.error(err);
      setRegFreqAlert({ severity: "error", message: "Failed to delete frequency." });
    }
  };

  const regionNames = useMemo(() => Object.keys(regFreqs).sort(), [regFreqs]);

  const renderRegionalFrequencies = () => {
    const isAdmin = user?.role === "admin";

    return (
      <Box>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6">Regional Frequency Reference</Typography>
          {isAdmin && (
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCreateRegFreq}>
              Add Frequency
            </Button>
          )}
        </Stack>

        {regFreqAlert && (
          <Alert severity={regFreqAlert.severity} sx={{ mb: 2 }} onClose={() => setRegFreqAlert(null)}>
            {regFreqAlert.message}
          </Alert>
        )}

        {regFreqsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
        ) : regionNames.length === 0 ? (
          <Alert severity="info">No regional frequencies configured yet. {isAdmin ? "Click 'Add Frequency' to get started." : "Contact an admin to add frequency data."}</Alert>
        ) : (
          <Stack spacing={3}>
            {regionNames.map((region) => (
              <Paper key={region} sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                  📻 {region}
                  <Chip label={`${regFreqs[region].length} ch`} size="small" variant="outlined" />
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Channel</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Frequency</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>TX Tone</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>RX Tone</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Mode</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>BW</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Assignment</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Notes</TableCell>
                        {isAdmin && <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {regFreqs[region].map((freq) => (
                        <TableRow key={freq.id} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{freq.channelName}</TableCell>
                          <TableCell sx={{ fontFamily: "monospace" }}>{freq.frequency}</TableCell>
                          <TableCell sx={{ fontFamily: "monospace" }}>{freq.txTone || "—"}</TableCell>
                          <TableCell sx={{ fontFamily: "monospace" }}>{freq.rxTone || "—"}</TableCell>
                          <TableCell>{freq.mode}</TableCell>
                          <TableCell>{freq.bandwidth || "—"}</TableCell>
                          <TableCell>{freq.assignment || "—"}</TableCell>
                          <TableCell sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {freq.notes || "—"}
                          </TableCell>
                          {isAdmin && (
                            <TableCell>
                              <IconButton size="small" onClick={() => openEditRegFreqDialog(freq)}><EditIcon fontSize="small" /></IconButton>
                              <IconButton size="small" color="error" onClick={() => handleDeleteRegFreq(freq.id)}><DeleteIcon fontSize="small" /></IconButton>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            ))}
          </Stack>
        )}

        {/* Regional Frequency Create/Edit Dialog */}
        <Dialog open={regFreqDialogOpen} onClose={() => !regFreqSaving && setRegFreqDialogOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>{regFreqDialogMode === "edit" ? "Edit Frequency" : "Add Frequency"}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="Region" value={regFreqForm.region} onChange={(e) => setRegFreqForm(p => ({ ...p, region: e.target.value }))} required fullWidth
                helperText="e.g. North Texas, Houston Metro, South Carolina"
              />
              <TextField label="Channel Name" value={regFreqForm.channelName} onChange={(e) => setRegFreqForm(p => ({ ...p, channelName: e.target.value }))} required fullWidth />
              <Stack direction="row" spacing={2}>
                <TextField label="Frequency (MHz)" value={regFreqForm.frequency} onChange={(e) => setRegFreqForm(p => ({ ...p, frequency: e.target.value }))} required fullWidth />
                <TextField select label="Mode" value={regFreqForm.mode} onChange={(e) => setRegFreqForm(p => ({ ...p, mode: e.target.value }))} fullWidth>
                  {["FM", "DMR", "P25", "NXDN", "DStar"].map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                </TextField>
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField label="TX Tone" value={regFreqForm.txTone} onChange={(e) => setRegFreqForm(p => ({ ...p, txTone: e.target.value }))} fullWidth />
                <TextField label="RX Tone" value={regFreqForm.rxTone} onChange={(e) => setRegFreqForm(p => ({ ...p, rxTone: e.target.value }))} fullWidth />
              </Stack>
              <Stack direction="row" spacing={2}>
                <TextField select label="Bandwidth" value={regFreqForm.bandwidth} onChange={(e) => setRegFreqForm(p => ({ ...p, bandwidth: e.target.value }))} fullWidth>
                  {["25 kHz", "12.5 kHz", "6.25 kHz"].map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                </TextField>
                <TextField label="Sort Order" value={regFreqForm.sortOrder} onChange={(e) => setRegFreqForm(p => ({ ...p, sortOrder: e.target.value }))} fullWidth type="number" />
              </Stack>
              <TextField label="Assignment" value={regFreqForm.assignment} onChange={(e) => setRegFreqForm(p => ({ ...p, assignment: e.target.value }))} fullWidth
                helperText="e.g. Primary Dispatch, Mutual Aid, TAC Channel"
              />
              <TextField label="Notes" value={regFreqForm.notes} onChange={(e) => setRegFreqForm(p => ({ ...p, notes: e.target.value }))} fullWidth multiline rows={2} />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRegFreqDialogOpen(false)} disabled={regFreqSaving}>Cancel</Button>
            <Button variant="contained" onClick={handleRegFreqSave} disabled={regFreqSaving || !regFreqForm.region || !regFreqForm.channelName || !regFreqForm.frequency}>
              {regFreqSaving ? "Saving..." : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "txdot-cams":
        return renderTxdotCams();
      case "ics-205":
        return renderICS205();
      case "repeater-map":
        return renderRepeaterMap();
      case "regional-frequencies":
        return renderRegionalFrequencies();
      case "patrol-calendar":
        return renderPatrolCalendar();
      case "policies":
        return renderPolicies();
      case "log-expense":
        return renderLogExpense();
      case "quick-links":
        return renderQuickLinks();
      case "prayer":
        return renderPrayer();
      case "awards":
        return renderAwards();
      case "region-map":
        return renderRegionMap();
      default:
        return null;
    }
  };

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              "& .MuiTab-root": {
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 1,
              },
            }}
          >
            {visibleTabs.map((tab) => (
              <Tab key={tab.value} label={tab.label} value={tab.value} />
            ))}
          </Tabs>

          <Box sx={{ p: 3 }}>{renderTabContent()}</Box>
        </Paper>
      </Box>

      {/* Policy Document Dialog */}
      <Dialog
        open={policyDialogOpen}
        onClose={handleClosePolicy}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            maxHeight: "90vh",
          },
        }}
      >
        {selectedPolicy && (
          <>
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                pb: 1,
              }}
            >
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, textTransform: "uppercase" }}>
                  {selectedPolicy.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Last Updated: {selectedPolicy.lastUpdated}
                </Typography>
              </Box>
              <IconButton onClick={handleClosePolicy} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ pt: 2 }}>
              {renderPolicyContent(selectedPolicy.content)}
            </DialogContent>
            <Divider />
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={handleClosePolicy} variant="outlined">
                Close
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  window.print();
                }}
              >
                Print
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </DashboardLayout>
  );
}

export default Resources;
